import { FormEvent, useEffect, useState } from "react";
import {
  Archive,
  BadgeCheck,
  Blocks,
  Building2,
  CopyPlus,
  KeyRound,
  LogIn,
  LogOut,
  Mail,
  PlayCircle,
  Plus,
  RefreshCw,
  Save,
  Search,
  Send,
  Server,
  ShieldCheck,
  SlidersHorizontal,
  ScrollText,
  Table2,
  Trash2,
  UserRound,
  WalletCards,
} from "lucide-react";
import {
  PLAN_BILLING_MODES,
  PLAN_CTA_KINDS,
  PRICING_CAPABILITY_FEATURE_KEYS,
  parsePlanMetadata,
  validatePlanMetadataContract,
  type PlanBillingMode,
  type PlanCtaKind,
} from "@aioscp/types";
import {
  api,
  type AgentReleaseEntry,
  type AuthenticatedUser,
  type ConnectorPluginAdapter,
  type ConnectorPluginCertification,
  type ConnectorPluginEntry,
  type ConnectorPluginStatus,
  type EntitlementOverrideGovernanceInput,
  type EntitlementOverrideInput,
  type MarketplaceBuyerReviewStatus,
  type MarketplacePackInstallAnalytics,
  type MarketplacePackEntry,
  type MarketplacePackReviewEntry,
  type MarketplacePackReviewStatus,
  type PackPublisher,
  type PackRevocation,
  type PlatformFeatureCatalogEntry,
  type PlatformFleetOverviewEntry,
  type PlatformModelProviderCredentialEntry,
  type PlanEntry,
  type PlanFeature,
  type PlanImpactPreview,
  type PlatformAuditEvent,
  type PlatformTransactionalEmailTestResult,
  type PlatformWorkspaceActivationAnalytics,
  type PlatformWorkspaceSummary,
  type PlatformTransactionalEmailSettings,
  type TransactionalEmailProvider,
  type RolloutPlanEntry,
  type SubscriptionEntry,
  type TenantEntitlementOverride,
} from "./api";

const TOKEN_KEY = "aioscp_superadmin_token";
const USER_KEY = "aioscp_superadmin_user";

type AppSection = "plans" | "features" | "workspaces" | "marketplace" | "operations" | "credentials" | "email" | "audit" | "account";
type WorkspaceStatusFilter = "all" | "active" | "suspended";
type PublisherStatusFilter = "all" | PackPublisher["status"];
type PackStatusFilter = "active" | "yanked" | "all";
type ReviewStatusFilter = MarketplacePackReviewStatus | "all";
type BuyerReviewStatusFilter = MarketplaceBuyerReviewStatus | "all";
type ConnectorPluginStatusFilter = ConnectorPluginStatus | "all";
type ConnectorPluginAdapterFilter = ConnectorPluginAdapter | "all";
type OperationsEnvironment = "dev" | "staging" | "production";
type RoiSnapshotType = "scheduled" | "qbr" | "renewal" | "manual";

interface TransactionalEmailFormState {
  provider: TransactionalEmailProvider;
  fromEmail: string;
  appBaseUrl: string;
  resendApiKey: string;
  sendGridApiKey: string;
  smtpHost: string;
  smtpPort: string;
  smtpUsername: string;
  smtpPassword: string;
  smtpSecure: boolean;
  reason: string;
}

function isTransactionalEmailProviderConfigured(settings: PlatformTransactionalEmailSettings | null): boolean {
  if (!settings) return false;
  if (settings.provider === "resend") return settings.resendConfigured;
  if (settings.provider === "sendgrid") return settings.sendGridConfigured;
  if (settings.provider === "smtp") return settings.smtpConfigured;
  return false;
}

function transactionalEmailProviderLabel(provider: TransactionalEmailProvider | undefined): string {
  if (provider === "resend") return "Resend";
  if (provider === "sendgrid") return "SendGrid";
  if (provider === "smtp") return "Google Workspace / SMTP";
  return "Local log";
}

function formatOperatorErrorMessage(message: string): string {
  if (/535[\s\S]*5\.7\.8|BadCredentials|Username and Password not accepted/i.test(message)) {
    return `Google Workspace rejected the SMTP login. Use a Google app password, not the normal mailbox password; confirm 2-Step Verification and app passwords are allowed for this account. Raw provider error: ${message}`;
  }
  return message;
}

const AVAILABILITY_FEATURE_PRESETS = [
  { featureKey: "connectors.plugins.manage", label: "BYO connector plugins" },
  { featureKey: "business.roi", label: "Business ROI evidence" },
  { featureKey: "marketplace.publisher.apply", label: "Publisher applications" },
  { featureKey: "marketplace.publish", label: "Marketplace publishing" },
] as const;

interface PlanDraft {
  planId: string;
  tier: string;
  displayName: string;
  description: string;
  active: boolean;
  features: PlanFeature[];
  metadataText: string;
  reason: string;
}

const DEFAULT_METADATA = {
  sortOrder: 40,
  public: false,
  billingMode: "manual",
  visibility: {
    pricingPage: false,
    tenantSettings: false,
    superadminOnly: true,
  },
};

const COMMERCIAL_METADATA_TEXT_FIELDS = [
  ["buyerSegment", "Buyer segment"],
  ["supportTier", "Support tier"],
  ["sla", "SLA"],
  ["securityReviewAssistance", "Security review"],
  ["deploymentAssistance", "Deployment assistance"],
  ["privateNetworking", "Private networking"],
  ["marketplaceTier", "Marketplace tier"],
  ["observabilityTier", "Observability tier"],
  ["rbacTier", "RBAC tier"],
  ["complianceTier", "Compliance tier"],
  ["rateLimitTier", "Rate limit tier"],
  ["analyticsTier", "Analytics tier"],
] as const;

const CHANGE_POLICY_KEYS = [
  ["allowSelfServeUpgrade", "Self-serve upgrade"],
  ["allowSelfServeDowngrade", "Self-serve downgrade"],
  ["requiresSales", "Requires sales"],
  ["requiresPaymentMethod", "Requires payment"],
] as const;

const VISIBILITY_KEYS = [
  ["pricingPage", "Pricing page"],
  ["tenantSettings", "Tenant settings"],
  ["superadminOnly", "SuperAdmin only"],
] as const;

const MODEL_ACCESS_FIELDS = [
  ["platformProviderIds", "Platform providers"],
  ["platformModelIds", "Platform model allowlist"],
  ["tenantProviderIds", "Tenant BYOK providers"],
  ["tenantModelIds", "Tenant model allowlist"],
] as const;

const DEFAULT_OPENAI_MODEL_OPTIONS = ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "o1", "o3", "o3-mini"];

const OVERRIDE_RISK_TIERS: readonly EntitlementOverrideGovernanceInput["riskTier"][] = ["low", "medium", "high", "critical"];

function readStoredUser(): AuthenticatedUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) as AuthenticatedUser : null;
  } catch {
    return null;
  }
}

function canUseSuperadmin(user: AuthenticatedUser | null): boolean {
  return user?.role === "platform:ops" || user?.role === "system";
}

function readStoredToken(): string | null {
  const user = readStoredUser();
  if (!canUseSuperadmin(user)) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return null;
  }
  return localStorage.getItem(TOKEN_KEY);
}

function formatJson(value: unknown): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatWholePercent(value: number | null): string {
  return value === null ? "n/a" : `${Math.round(value)}%`;
}

function formatCapacity(current: number, capacity: number): string {
  return capacity > 0 ? `${current.toLocaleString()} / ${capacity.toLocaleString()}` : "No capacity";
}

function parseJsonObject(value: string): Readonly<Record<string, unknown>> {
  const parsed = JSON.parse(value) as unknown;
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Metadata must be a JSON object");
  }
  return parsed as Readonly<Record<string, unknown>>;
}

function parseJsonObjectDraft(value: string): { metadata: Record<string, unknown> | null; error: string | null } {
  try {
    return { metadata: { ...parseJsonObject(value) }, error: null };
  } catch (caught) {
    return { metadata: null, error: caught instanceof Error ? caught.message : String(caught) };
  }
}

function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? { ...value as Record<string, unknown> }
    : {};
}

function stringValue(metadata: Readonly<Record<string, unknown>>, key: string): string {
  const value = metadata[key];
  return typeof value === "string" ? value : "";
}

function nestedStringValue(metadata: Readonly<Record<string, unknown>>, group: string, key: string): string {
  const value = recordValue(metadata[group])[key];
  return typeof value === "string" ? value : "";
}

function booleanValue(metadata: Readonly<Record<string, unknown>>, key: string, fallback: boolean): boolean {
  const value = metadata[key];
  return typeof value === "boolean" ? value : fallback;
}

function nestedBooleanValue(metadata: Readonly<Record<string, unknown>>, group: string, key: string, fallback: boolean): boolean {
  const value = recordValue(metadata[group])[key];
  return typeof value === "boolean" ? value : fallback;
}

function numberInputValue(metadata: Readonly<Record<string, unknown>>, key: string): string {
  const value = metadata[key];
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function stringArrayInputValue(metadata: Readonly<Record<string, unknown>>, key: string): string {
  const value = metadata[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string").join(", ") : "";
}

function nestedStringArrayInputValue(metadata: Readonly<Record<string, unknown>>, group: string, key: string): string {
  const value = recordValue(metadata[group])[key];
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string").join(", ") : "";
}

function parseStringArrayInput(value: string): string[] {
  return value.split(",").map((entry) => entry.trim()).filter(Boolean);
}

function defaultOverrideExpiresAt(): string {
  const expiresAt = new Date();
  expiresAt.setUTCDate(expiresAt.getUTCDate() + 30);
  return expiresAt.toISOString();
}

function requiresApprovalWorkflow(riskTier?: EntitlementOverrideGovernanceInput["riskTier"]): boolean {
  return riskTier === "high" || riskTier === "critical";
}

function mergeOverrideGovernance(
  current: EntitlementOverrideInput["governance"],
  update: Partial<EntitlementOverrideGovernanceInput>,
): EntitlementOverrideGovernanceInput {
  const approvalId = update.approvalId !== undefined ? update.approvalId : current?.approvalId;
  const comment = update.comment !== undefined ? update.comment : current?.comment;
  return {
    ticketId: update.ticketId ?? current?.ticketId ?? "",
    approvedBy: update.approvedBy ?? current?.approvedBy ?? "",
    riskTier: update.riskTier ?? current?.riskTier ?? "medium",
    ...(approvalId ? { approvalId } : {}),
    ...(comment ? { comment } : {}),
  };
}

function draftFromPlan(plan: PlanEntry): PlanDraft {
  return {
    planId: plan.planId,
    tier: plan.tier,
    displayName: plan.displayName,
    description: plan.description,
    active: plan.active ?? false,
    features: plan.features.map((feature) => ({ ...feature })),
    metadataText: formatJson(plan.metadata ?? {}),
    reason: "",
  };
}

function newPlanDraft(index: number): PlanDraft {
  return {
    planId: `plan-${index + 1}`,
    tier: "business",
    displayName: "Business",
    description: "Business plan",
    active: false,
    features: [],
    metadataText: formatJson(DEFAULT_METADATA),
    reason: "",
  };
}

function preferredFeatureKind(
  catalogEntry?: PlatformFeatureCatalogEntry,
  sourceFeatures: readonly PlanFeature[] = [],
): PlanFeature["kind"] {
  if (catalogEntry?.kind === "flag" || catalogEntry?.kind === "quota") {
    return catalogEntry.kind;
  }

  const sourceKinds = [
    ...(catalogEntry?.sources.map((source) => source.kind) ?? []),
    ...sourceFeatures.map((feature) => feature.kind),
  ];
  const quotaCount = sourceKinds.filter((kind) => kind === "quota").length;
  const flagCount = sourceKinds.filter((kind) => kind === "flag").length;
  return quotaCount > flagCount ? "quota" : "flag";
}

function defaultFeatureUnit(
  catalogEntry?: PlatformFeatureCatalogEntry,
  sourceFeatures: readonly PlanFeature[] = [],
): string | undefined {
  return catalogEntry?.quotaPlans.find((plan) => typeof plan.unit === "string" && plan.unit.length > 0)?.unit
    ?? catalogEntry?.sources.find((source) => typeof source.unit === "string" && source.unit.length > 0)?.unit
    ?? sourceFeatures.find((feature) => typeof feature.unit === "string" && feature.unit.length > 0)?.unit;
}

function defaultFeatureDescription(
  catalogEntry?: PlatformFeatureCatalogEntry,
  sourceFeatures: readonly PlanFeature[] = [],
): string | undefined {
  return catalogEntry?.description
    ?? catalogEntry?.sources.find((source) => typeof source.description === "string" && source.description.length > 0)?.description
    ?? sourceFeatures.find((feature) => typeof feature.description === "string" && feature.description.length > 0)?.description;
}

function createDefaultPlanFeature(
  featureKey: string,
  catalogEntry?: PlatformFeatureCatalogEntry,
  sourceFeatures: readonly PlanFeature[] = [],
): PlanFeature {
  const kind = preferredFeatureKind(catalogEntry, sourceFeatures);
  const unit = defaultFeatureUnit(catalogEntry, sourceFeatures);
  const description = defaultFeatureDescription(catalogEntry, sourceFeatures);
  return {
    featureKey,
    kind,
    enabled: false,
    ...(kind === "quota" ? { limit: 0 } : {}),
    ...(unit !== undefined ? { unit } : {}),
    ...(description !== undefined ? { description } : {}),
  };
}

export function createMissingCatalogFeatureRows(
  featureKeys: readonly string[],
  catalogEntries: readonly PlatformFeatureCatalogEntry[],
  plans: readonly PlanEntry[],
  existingFeatures: readonly PlanFeature[] = [],
): PlanFeature[] {
  const catalogByKey = new Map(catalogEntries.map((entry) => [entry.featureKey, entry]));
  const existingKeys = new Set(existingFeatures
    .map((feature) => feature.featureKey.trim())
    .filter((featureKey) => featureKey.length > 0));
  const seen = new Set<string>();

  return featureKeys
    .map((featureKey) => featureKey.trim())
    .filter((featureKey) => {
      if (featureKey.length === 0 || seen.has(featureKey) || existingKeys.has(featureKey)) return false;
      seen.add(featureKey);
      return true;
    })
    .map((featureKey) => {
      const sourceFeatures = plans.flatMap((plan) => plan.features.filter((feature) => feature.featureKey === featureKey));
      return createDefaultPlanFeature(featureKey, catalogByKey.get(featureKey), sourceFeatures);
    });
}

function normalizeFeatures(features: readonly PlanFeature[]): PlanFeature[] {
  return features
    .filter((feature) => feature.featureKey.trim().length > 0)
    .map((feature) => ({
      featureKey: feature.featureKey.trim(),
      kind: feature.kind,
      enabled: feature.enabled,
      ...(feature.kind === "quota" ? { limit: feature.limit ?? null } : {}),
      ...(feature.unit?.trim() ? { unit: feature.unit.trim() } : {}),
      ...(feature.description?.trim() ? { description: feature.description.trim() } : {}),
    }));
}

function statusTone(plan: PlanEntry): string {
  if (plan.metadata && typeof plan.metadata["billingMode"] === "string") {
    return plan.metadata["billingMode"] as string;
  }
  return plan.active ? "active" : "draft";
}

function featureValue(plan: PlanEntry, featureKey: string): string {
  const feature = plan.features.find((candidate) => candidate.featureKey === featureKey);
  if (!feature) return "-";
  if (feature.kind === "quota") return feature.limit === null || feature.limit === undefined ? "unlimited" : String(feature.limit);
  return feature.enabled ? "on" : "off";
}

function onboardingBlockerCount(workspace: PlatformWorkspaceSummary): number {
  return workspace.onboarding?.blockerCount ?? 0;
}

function onboardingLabel(workspace: PlatformWorkspaceSummary): string {
  if (!workspace.onboarding) return "Onboarding unknown";
  return workspace.onboarding.completed ? "Onboarding complete" : "Onboarding pending";
}

function onboardingTone(workspace: PlatformWorkspaceSummary): string {
  if (!workspace.onboarding) return "draft";
  if (workspace.onboarding.blockerCount > 0) return "warning";
  return workspace.onboarding.completed ? "live" : "warning";
}

function isolationLabel(value: PlatformWorkspaceSummary["isolationTier"]): string {
  switch (value) {
    case "dedicated-deployment":
      return "Dedicated deployment";
    case "dedicated-execution":
      return "Dedicated execution";
    default:
      return "Shared SaaS";
  }
}

export function SuperadminConsole() {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState<AuthenticatedUser | null>(() => {
    const stored = readStoredUser();
    return canUseSuperadmin(stored) ? stored : null;
  });
  const [section, setSection] = useState<AppSection>("plans");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordChangeForm, setPasswordChangeForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [plans, setPlans] = useState<PlanEntry[]>([]);
  const [features, setFeatures] = useState<PlatformFeatureCatalogEntry[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [cloneTargetPlanId, setCloneTargetPlanId] = useState("");
  const [preview, setPreview] = useState<PlanImpactPreview | null>(null);
  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const [workspaceStatus, setWorkspaceStatus] = useState<WorkspaceStatusFilter>("all");
  const [workspaces, setWorkspaces] = useState<PlatformWorkspaceSummary[]>([]);
  const [workspaceActivationAnalytics, setWorkspaceActivationAnalytics] = useState<PlatformWorkspaceActivationAnalytics | null>(null);
  const [tenantId, setTenantId] = useState("acme");
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [subscription, setSubscription] = useState<SubscriptionEntry | null>(null);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [subscriptionReason, setSubscriptionReason] = useState("");
  const [overrides, setOverrides] = useState<TenantEntitlementOverride[]>([]);
  const [auditEvents, setAuditEvents] = useState<PlatformAuditEvent[]>([]);
  const [auditTenantId, setAuditTenantId] = useState("");
  const [auditAction, setAuditAction] = useState("");
  const [auditResource, setAuditResource] = useState("");
  const [platformCredentials, setPlatformCredentials] = useState<PlatformModelProviderCredentialEntry[]>([]);
  const [openAiRotationKey, setOpenAiRotationKey] = useState("");
  const [openAiRotationReason, setOpenAiRotationReason] = useState("");
  const [transactionalEmailSettings, setTransactionalEmailSettings] = useState<PlatformTransactionalEmailSettings | null>(null);
  const [transactionalEmailForm, setTransactionalEmailForm] = useState<TransactionalEmailFormState>({
    provider: "log",
    fromEmail: "AIOSCP Platform <no-reply@aioscp.local>",
    appBaseUrl: "http://localhost:3000",
    resendApiKey: "",
    sendGridApiKey: "",
    smtpHost: "smtp.gmail.com",
    smtpPort: "465",
    smtpUsername: "",
    smtpPassword: "",
    smtpSecure: true,
    reason: "",
  });
  const [transactionalEmailTestRecipient, setTransactionalEmailTestRecipient] = useState("rushilinfra@gmail.com");
  const [transactionalEmailTestResult, setTransactionalEmailTestResult] = useState<PlatformTransactionalEmailTestResult | null>(null);
  const [publisherStatus, setPublisherStatus] = useState<PublisherStatusFilter>("all");
  const [publishers, setPublishers] = useState<PackPublisher[]>([]);
  const [publisherForm, setPublisherForm] = useState({ publisherId: "", displayName: "", contactEmail: "", publicKeyBase64: "", notes: "" });
  const [publisherSuspendReason, setPublisherSuspendReason] = useState("");
  const [packStatus, setPackStatus] = useState<PackStatusFilter>("active");
  const [packReviewStatus, setPackReviewStatus] = useState<ReviewStatusFilter>("all");
  const [packPublisherId, setPackPublisherId] = useState("");
  const [marketplacePacks, setMarketplacePacks] = useState<MarketplacePackEntry[]>([]);
  const [marketplaceInstallAnalytics, setMarketplaceInstallAnalytics] = useState<MarketplacePackInstallAnalytics | null>(null);
  const [marketplaceReviews, setMarketplaceReviews] = useState<MarketplacePackReviewEntry[]>([]);
  const [buyerReviewStatus, setBuyerReviewStatus] = useState<BuyerReviewStatusFilter>("pending");
  const [buyerReviewNotes, setBuyerReviewNotes] = useState("");
  const [packReviewForm, setPackReviewForm] = useState({ packId: "", version: "", status: "community_review" as MarketplacePackReviewStatus, notes: "", evidenceUrls: "" });
  const [revocations, setRevocations] = useState<PackRevocation[]>([]);
  const [revocationForm, setRevocationForm] = useState({ packId: "", version: "", reason: "" });
  const [connectorTenantId, setConnectorTenantId] = useState("acme");
  const [connectorStatus, setConnectorStatus] = useState<ConnectorPluginStatusFilter>("all");
  const [connectorAdapter, setConnectorAdapter] = useState<ConnectorPluginAdapterFilter>("all");
  const [connectorPlugins, setConnectorPlugins] = useState<ConnectorPluginEntry[]>([]);
  const [connectorForm, setConnectorForm] = useState({ pluginId: "", version: "", notes: "", reason: "" });
  const [connectorPluginDetail, setConnectorPluginDetail] = useState<ConnectorPluginEntry | null>(null);
  const [connectorCertification, setConnectorCertification] = useState<ConnectorPluginCertification | null>(null);
  const [connectorEvidencePack, setConnectorEvidencePack] = useState<unknown>(null);
  const [operationsTenantId, setOperationsTenantId] = useState("acme");
  const [operationsAgentId, setOperationsAgentId] = useState("");
  const [operationsEnvironment, setOperationsEnvironment] = useState<OperationsEnvironment>("production");
  const [operationsRollbackReason, setOperationsRollbackReason] = useState("");
  const [releases, setReleases] = useState<AgentReleaseEntry[]>([]);
  const [rolloutPlan, setRolloutPlan] = useState<RolloutPlanEntry | null>(null);
  const [fleetOverview, setFleetOverview] = useState<PlatformFleetOverviewEntry | null>(null);
  const [roiSummary, setRoiSummary] = useState<unknown>(null);
  const [roiWorkflows, setRoiWorkflows] = useState<unknown[]>([]);
  const [roiSnapshots, setRoiSnapshots] = useState<unknown[]>([]);
  const [roiSnapshotType, setRoiSnapshotType] = useState<RoiSnapshotType>("manual");
  const [overrideForm, setOverrideForm] = useState<EntitlementOverrideInput>({
    featureKey: "",
    enabled: true,
    expiresAt: defaultOverrideExpiresAt(),
    governance: {
      ticketId: "",
      approvedBy: "",
      riskTier: "medium",
    },
    reason: "",
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const selectedPlan = plans.find((plan) => plan.planId === selectedPlanId) ?? null;
  const selectedWorkspace = workspaces.find((workspace) => workspace.tenantId === tenantId) ?? null;
  const openAiCredential = platformCredentials.find((credential) => credential.providerId === "openai") ?? null;
  const draftMetadataState = draft ? parseJsonObjectDraft(draft.metadataText) : null;
  const draftMetadataRecord = draftMetadataState?.metadata ?? {};
  const passwordChangeRequired = user?.passwordChangeRequired === true;
  const passwordChangeSubmitDisabled = loading
    || !passwordChangeForm.currentPassword
    || !passwordChangeForm.newPassword
    || !passwordChangeForm.confirmPassword
    || passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword;
  const platformModelOptions = Array.from(new Set([
    ...DEFAULT_OPENAI_MODEL_OPTIONS,
    ...platformCredentials.flatMap((credential) => credential.models),
    ...parseStringArrayInput(nestedStringArrayInputValue(draftMetadataRecord, "modelAccess", "platformModelIds")),
  ])).sort();
  const parsedDraftMetadata = draft && draftMetadataState?.metadata
    ? parsePlanMetadata({
      planId: (draft.planId.trim() || selectedPlan?.planId) ?? "draft",
      tier: draft.tier.trim(),
      active: draft.active,
      metadata: draftMetadataState.metadata,
    })
    : null;
  const metadataValidationIssues = draft
    ? draftMetadataState?.metadata
      ? validatePlanMetadataContract({
        planId: (draft.planId.trim() || selectedPlan?.planId) ?? "draft",
        tier: draft.tier.trim(),
        active: draft.active,
        metadata: draftMetadataState.metadata,
      })
      : [{ path: "metadata", message: draftMetadataState?.error ?? "must be valid JSON" }]
    : [];
  const featureRegistry = Array.from(new Set([
    ...PRICING_CAPABILITY_FEATURE_KEYS,
    ...features.map((feature) => feature.featureKey),
    ...plans.flatMap((plan) => plan.features.map((feature) => feature.featureKey)),
  ])).sort();
  const testEmailRecipientReady = transactionalEmailTestRecipient.trim().length >= 5;
  const testEmailSelectedProviderCanSend = transactionalEmailForm.provider !== "log";
  const testEmailSavedProviderMatchesSelection = transactionalEmailSettings?.provider === transactionalEmailForm.provider;
  const testEmailGuidance = !testEmailRecipientReady
    ? "Enter a recipient email."
    : !testEmailSelectedProviderCanSend
      ? "Choose Resend, SendGrid, or Google Workspace / SMTP."
      : !testEmailSavedProviderMatchesSelection
        ? "Save email settings before sending a test."
        : transactionalEmailSettings && !isTransactionalEmailProviderConfigured(transactionalEmailSettings)
          ? "Saved provider is missing credentials."
          : null;

  async function run<T>(operation: () => Promise<T>, success?: string): Promise<T | null> {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await operation();
      if (success) setNotice(success);
      return result;
    } catch (caught) {
      setError(formatOperatorErrorMessage(caught instanceof Error ? caught.message : String(caught)));
      return null;
    } finally {
      setLoading(false);
    }
  }

  function draftMetadata(): Readonly<Record<string, unknown>> | null {
    if (!draft) return null;
    try {
      return parseJsonObject(draft.metadataText);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      return null;
    }
  }

  function updateMetadata(mutator: (metadata: Record<string, unknown>) => void) {
    if (!draft) return;
    const current = parseJsonObjectDraft(draft.metadataText).metadata ?? {};
    const next = { ...current };
    mutator(next);
    setDraft({ ...draft, metadataText: formatJson(next) });
  }

  function setMetadataString(key: string, value: string) {
    updateMetadata((metadata) => {
      const trimmed = value.trim();
      if (trimmed) metadata[key] = trimmed;
      else delete metadata[key];
    });
  }

  function setMetadataNumber(key: string, value: string) {
    updateMetadata((metadata) => {
      if (value.trim() === "") {
        delete metadata[key];
        return;
      }
      metadata[key] = Number(value);
    });
  }

  function setMetadataBoolean(key: string, value: boolean) {
    updateMetadata((metadata) => {
      metadata[key] = value;
    });
  }

  function setMetadataStringArray(key: string, value: string) {
    updateMetadata((metadata) => {
      const entries = parseStringArrayInput(value);
      if (entries.length > 0) metadata[key] = entries;
      else delete metadata[key];
    });
  }

  function setNestedMetadataStringArray(group: string, key: string, value: string) {
    updateMetadata((metadata) => {
      const nested = recordValue(metadata[group]);
      const entries = parseStringArrayInput(value);
      if (entries.length > 0) nested[key] = entries;
      else delete nested[key];
      if (Object.keys(nested).length > 0) metadata[group] = nested;
      else delete metadata[group];
    });
  }

  function setNestedMetadataStringArrayValues(group: string, key: string, values: readonly string[]) {
    updateMetadata((metadata) => {
      const nested = recordValue(metadata[group]);
      const entries = values.map((entry) => entry.trim()).filter(Boolean);
      if (entries.length > 0) nested[key] = Array.from(new Set(entries));
      else delete nested[key];
      if (Object.keys(nested).length > 0) metadata[group] = nested;
      else delete metadata[group];
    });
  }

  function setNestedMetadataString(group: string, key: string, value: string) {
    updateMetadata((metadata) => {
      const nested = recordValue(metadata[group]);
      const trimmed = value.trim();
      if (trimmed) nested[key] = trimmed;
      else delete nested[key];
      if (Object.keys(nested).length > 0) metadata[group] = nested;
      else delete metadata[group];
    });
  }

  function setNestedMetadataBoolean(group: string, key: string, value: boolean) {
    updateMetadata((metadata) => {
      metadata[group] = { ...recordValue(metadata[group]), [key]: value };
    });
  }

  function overrideGovernanceInput(): EntitlementOverrideGovernanceInput | null {
    const governance = overrideForm.governance;
    const ticketId = governance?.ticketId.trim() ?? "";
    const approvedBy = governance?.approvedBy.trim() ?? "";
    const approvalId = governance?.approvalId?.trim();
    const comment = governance?.comment?.trim();
    if (!ticketId || !approvedBy) {
      setError("Override governance requires an approval ticket and a second operator.");
      return null;
    }
    if (requiresApprovalWorkflow(governance?.riskTier) && !approvalId) {
      setError("High-risk override governance requires a linked approval workflow ID.");
      return null;
    }
    return {
      ticketId,
      approvedBy,
      riskTier: governance?.riskTier ?? "medium",
      ...(approvalId ? { approvalId } : {}),
      ...(comment ? { comment } : {}),
    };
  }

  async function loadPlans(activeToken = token, preferredPlanId = selectedPlanId) {
    if (!activeToken) return;
    const result = await run(() => api.plans(activeToken));
    if (!result) return;
    setPlans(result.plans);
    const nextSelected = preferredPlanId && result.plans.some((plan) => plan.planId === preferredPlanId)
      ? preferredPlanId
      : result.plans[0]?.planId ?? null;
    setSelectedPlanId(nextSelected);
    const plan = result.plans.find((entry) => entry.planId === nextSelected) ?? result.plans[0];
    setDraft(plan ? draftFromPlan(plan) : newPlanDraft(0));
  }

  async function loadFeatureCatalog(activeToken = token) {
    if (!activeToken) return;
    const result = await run(() => api.features(activeToken));
    if (result) setFeatures(result.features);
  }

  async function loadPlatformCredentials(activeToken = token) {
    if (!activeToken) return;
    const result = await run(() => api.platformModelProviderCredentials(activeToken));
    if (result) setPlatformCredentials(result.providers);
  }

  async function loadTransactionalEmailSettings(activeToken = token) {
    if (!activeToken) return;
    const result = await run(() => api.platformTransactionalEmailSettings(activeToken));
    if (!result) return;
    setTransactionalEmailSettings(result.settings);
    setTransactionalEmailTestResult(null);
    setTransactionalEmailForm((current) => ({
      ...current,
      provider: result.settings.provider,
      fromEmail: result.settings.fromEmail,
      appBaseUrl: result.settings.appBaseUrl,
      resendApiKey: "",
      sendGridApiKey: "",
      smtpHost: result.settings.smtpHost ?? "smtp.gmail.com",
      smtpPort: String(result.settings.smtpPort ?? 465),
      smtpUsername: result.settings.smtpUsername ?? "",
      smtpPassword: "",
      smtpSecure: result.settings.smtpSecure,
    }));
  }

  async function loadWorkspaces(activeToken = token) {
    if (!activeToken) return;
    const filters = {
      ...(workspaceQuery.trim() ? { query: workspaceQuery.trim() } : {}),
      ...(workspaceStatus !== "all" ? { status: workspaceStatus } : {}),
      limit: 200,
    };
    const result = await run(async () => {
      const [workspaceResult, analyticsResult] = await Promise.all([
        api.workspaces(activeToken, filters),
        api.workspaceActivationAnalytics(activeToken, filters),
      ]);
      return { workspaceResult, analyticsResult };
    });
    if (!result) return;
    setWorkspaces(result.workspaceResult.workspaces);
    setWorkspaceActivationAnalytics(result.analyticsResult.analytics);
    if (!tenantId && result.workspaceResult.workspaces[0]) {
      setTenantId(result.workspaceResult.workspaces[0].tenantId);
    }
  }

  async function loadWorkspace(activeToken = token, activeTenantId = tenantId) {
    if (!activeToken || !activeTenantId.trim()) return;
    const result = await run(async () => {
      const [subscriptionResult, overrideResult] = await Promise.all([
        api.workspaceSubscription(activeToken, activeTenantId.trim()),
        api.entitlementOverrides(activeToken, activeTenantId.trim()),
      ]);
      return { subscriptionResult, overrideResult };
    });
    if (!result) return;
    setSubscription(result.subscriptionResult.subscription);
    setSubscriptionPlanId(result.subscriptionResult.subscription.planId);
    setOverrides(result.overrideResult.overrides);
  }

  async function loadAuditEvents(activeToken = token) {
    if (!activeToken) return;
    const result = await run(() => api.auditEvents(activeToken, {
      ...(auditTenantId.trim() ? { tenantId: auditTenantId.trim() } : {}),
      ...(auditAction.trim() ? { action: auditAction.trim() } : {}),
      ...(auditResource.trim() ? { resource: auditResource.trim() } : {}),
      limit: 200,
    }));
    if (result) setAuditEvents(result.entries);
  }

  async function loadMarketplaceOps(activeToken = token) {
    if (!activeToken) return;
    const result = await run(async () => {
      const [publisherResult, packResult, revocationResult, installAnalyticsResult, reviewResult] = await Promise.all([
        api.packPublishers(activeToken, publisherStatus),
        api.marketplacePacks(activeToken, {
          publisherId: packPublisherId,
          status: packStatus,
          reviewStatus: packReviewStatus,
          includeRevoked: true,
          limit: 100,
        }),
        api.packRevocations(activeToken),
        api.marketplaceInstallAnalytics(activeToken, { limit: 500 }),
        api.marketplaceReviews(activeToken, { status: buyerReviewStatus, limit: 100 }),
      ]);
      return { publisherResult, packResult, revocationResult, installAnalyticsResult, reviewResult };
    });
    if (!result) return;
    setPublishers(result.publisherResult.publishers);
    setMarketplacePacks(result.packResult.packs);
    setRevocations(result.revocationResult.revocations);
    setMarketplaceInstallAnalytics(result.installAnalyticsResult.analytics);
    setMarketplaceReviews(result.reviewResult.reviews);
  }

  async function loadConnectorPlugins(activeToken = token) {
    const activeTenantId = connectorTenantId.trim();
    if (!activeToken || !activeTenantId) return;
    const result = await run(() => api.connectorPlugins(activeToken, activeTenantId, {
      status: connectorStatus,
      adapter: connectorAdapter,
    }));
    if (!result) return;
    setConnectorPlugins(result.plugins);
  }

  async function loadTenantOperations(activeToken = token) {
    const activeTenantId = operationsTenantId.trim();
    if (!activeToken || !activeTenantId) return;
    const result = await run(async () => {
      const [releaseResult, fleetResult, roiSummaryResult, roiWorkflowResult, roiSnapshotResult] = await Promise.all([
        api.tenantReleases(activeToken, activeTenantId, {
          ...(operationsAgentId.trim() ? { agentId: operationsAgentId.trim() } : {}),
          environment: operationsEnvironment,
          limit: 50,
        }),
        api.platformFleetOverview(activeToken, { limit: 200 }),
        api.tenantRoiSummary(activeToken, activeTenantId),
        api.tenantRoiWorkflows(activeToken, activeTenantId),
        api.tenantRoiSnapshots(activeToken, activeTenantId),
      ]);
      const rolloutResult = operationsAgentId.trim()
        ? await api.tenantRollout(activeToken, activeTenantId, operationsAgentId.trim(), operationsEnvironment)
        : { plan: null };
      return { releaseResult, fleetResult, rolloutResult, roiSummaryResult, roiWorkflowResult, roiSnapshotResult };
    });
    if (!result) return;
    setReleases(result.releaseResult.releases);
    setFleetOverview(result.fleetResult.fleet);
    setRolloutPlan(result.rolloutResult.plan);
    setRoiSummary(result.roiSummaryResult.summary);
    setRoiWorkflows(result.roiWorkflowResult.workflows);
    setRoiSnapshots(result.roiSnapshotResult.snapshots);
  }

  async function refreshSection(activeSection = section) {
    if (activeSection === "plans") return loadPlans();
    if (activeSection === "features") return loadFeatureCatalog();
    if (activeSection === "workspaces") return loadWorkspaces();
    if (activeSection === "marketplace") return Promise.all([loadMarketplaceOps(), loadConnectorPlugins()]);
    if (activeSection === "operations") return loadTenantOperations();
    if (activeSection === "credentials") return loadPlatformCredentials();
    if (activeSection === "email") return loadTransactionalEmailSettings();
    if (activeSection === "account") return;
    return loadAuditEvents();
  }

  async function loadInitialData(activeToken: string) {
    await Promise.all([loadPlans(activeToken), loadFeatureCatalog(activeToken), loadWorkspaces(activeToken), loadPlatformCredentials(activeToken), loadTransactionalEmailSettings(activeToken), loadAuditEvents(activeToken), loadMarketplaceOps(activeToken), loadConnectorPlugins(activeToken), loadTenantOperations(activeToken)]);
  }

  useEffect(() => {
    if (!token) return;
    void (async () => {
      const current = await run(() => api.me(token));
      if (!current) return;
      if (!canUseSuperadmin(current.user)) {
        setError("Superadmin requires a platform:ops or system account.");
        logout();
        return;
      }
      localStorage.setItem(USER_KEY, JSON.stringify(current.user));
      setUser(current.user);
      if (current.user.passwordChangeRequired) {
        setSection("account");
        return;
      }
      await loadInitialData(token);
    })();
  }, [token]);

  useEffect(() => {
    if (!selectedPlan) return;
    setDraft(draftFromPlan(selectedPlan));
    setCloneTargetPlanId(`${selectedPlan.planId}-copy`);
    setPreview(null);
  }, [selectedPlanId]);

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = await run(() => api.login(email, password), "Signed in");
    if (!result) return;
    if (!canUseSuperadmin(result.user)) {
      setError("Superadmin requires a platform:ops or system account.");
      return;
    }
    localStorage.setItem(TOKEN_KEY, result.token);
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    setToken(result.token);
    setUser(result.user);
    setPassword("");
    if (result.user.passwordChangeRequired) {
      setSection("account");
    }
  }

  async function handlePasswordChange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    if (passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword) {
      setError("New password and confirmation must match.");
      return;
    }
    const result = await run(() => api.changePassword(token, {
      currentPassword: passwordChangeForm.currentPassword,
      newPassword: passwordChangeForm.newPassword,
    }), "Password changed");
    if (!result) return;
    localStorage.setItem(TOKEN_KEY, result.token);
    localStorage.setItem(USER_KEY, JSON.stringify(result.user));
    setToken(result.token);
    setUser(result.user);
    setPasswordChangeForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setSection("plans");
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    setPasswordChangeForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPlans([]);
    setSelectedPlanId(null);
    setDraft(null);
    setPreview(null);
    setSubscription(null);
    setOverrides([]);
    setWorkspaces([]);
    setWorkspaceActivationAnalytics(null);
    setAuditEvents([]);
    setFleetOverview(null);
    setFeatures([]);
    setPlatformCredentials([]);
    setOpenAiRotationKey("");
    setOpenAiRotationReason("");
    setTransactionalEmailSettings(null);
    setTransactionalEmailForm({
      provider: "log",
      fromEmail: "AIOSCP Platform <no-reply@aioscp.local>",
      appBaseUrl: "http://localhost:3000",
      resendApiKey: "",
      sendGridApiKey: "",
      smtpHost: "smtp.gmail.com",
      smtpPort: "465",
      smtpUsername: "",
      smtpPassword: "",
      smtpSecure: true,
      reason: "",
    });
    setTransactionalEmailTestRecipient("rushilinfra@gmail.com");
    setTransactionalEmailTestResult(null);
    setPublishers([]);
    setMarketplacePacks([]);
    setMarketplaceInstallAnalytics(null);
    setMarketplaceReviews([]);
    setRevocations([]);
    setConnectorPlugins([]);
    setConnectorPluginDetail(null);
    setConnectorCertification(null);
    setConnectorEvidencePack(null);
    setReleases([]);
    setRolloutPlan(null);
    setRoiSummary(null);
    setRoiWorkflows([]);
    setRoiSnapshots([]);
  }

  function renderAccountSection(forced: boolean) {
    return (
      <section className={`account-workbench ${forced ? "required" : ""}`}>
        <section className="panel account-panel">
          <div className="section-title">
            <UserRound size={19} />
            <h2>Profile</h2>
          </div>
          <div className="credential-meta account-meta">
            <span>Email</span>
            <strong>{user?.email ?? "-"}</strong>
            <span>Role</span>
            <strong>{user?.role ?? "-"}</strong>
            <span>Tenant</span>
            <strong>{user?.tenantId ?? "-"}</strong>
            <span>Password</span>
            <strong><span className={`status-pill ${forced ? "warning" : "live"}`}>{forced ? "change required" : "active"}</span></strong>
          </div>
        </section>

        <section className="panel account-panel">
          <div className="section-title">
            <KeyRound size={19} />
            <h2>{forced ? "Change Password" : "Rotate Password"}</h2>
          </div>
          {forced ? <div className="validation-panel">Password change required before opening the Superadmin console.</div> : null}
          <form className="credential-form-grid" onSubmit={(event) => void handlePasswordChange(event)}>
            <label>
              Current password
              <input value={passwordChangeForm.currentPassword} onChange={(event) => setPasswordChangeForm({ ...passwordChangeForm, currentPassword: event.currentTarget.value })} type="password" autoComplete="current-password" />
            </label>
            <label>
              New password
              <input value={passwordChangeForm.newPassword} onChange={(event) => setPasswordChangeForm({ ...passwordChangeForm, newPassword: event.currentTarget.value })} type="password" autoComplete="new-password" />
            </label>
            <label>
              Confirm new password
              <input value={passwordChangeForm.confirmPassword} onChange={(event) => setPasswordChangeForm({ ...passwordChangeForm, confirmPassword: event.currentTarget.value })} type="password" autoComplete="new-password" />
            </label>
            {passwordChangeForm.confirmPassword && passwordChangeForm.newPassword !== passwordChangeForm.confirmPassword ? <div className="validation-panel">New password and confirmation must match.</div> : null}
            <small>Use at least 12 characters with lowercase, uppercase, number, and symbol characters.</small>
            <button className="primary-button" type="submit" disabled={passwordChangeSubmitDisabled}>
              <Save size={17} /> Save password
            </button>
          </form>
        </section>
      </section>
    );
  }

  function startNewPlan() {
    const baseDraft = newPlanDraft(plans.length);
    setSelectedPlanId(null);
    setDraft({
      ...baseDraft,
      features: createMissingCatalogFeatureRows(featureRegistry, features, plans),
    });
    setCloneTargetPlanId("");
    setPreview(null);
  }

  function updateFeature(index: number, changes: Partial<PlanFeature>) {
    if (!draft) return;
    const features = draft.features.map((feature, featureIndex) => {
      if (featureIndex !== index) return feature;
      const nextFeature = { ...feature, ...changes };
      return nextFeature.kind === "flag" ? { ...nextFeature, limit: undefined } : nextFeature;
    });
    setDraft({ ...draft, features });
  }

  function addFeature() {
    if (!draft) return;
    const retainedFeatures = draft.features.filter((feature) => feature.featureKey.trim().length > 0);
    const additions = createMissingCatalogFeatureRows(featureRegistry, features, plans, retainedFeatures);
    if (additions.length > 0) {
      setDraft({
        ...draft,
        features: [...retainedFeatures, ...additions],
      });
      setNotice(`Added ${additions.length} catalog feature${additions.length === 1 ? "" : "s"}.`);
      return;
    }

    if (retainedFeatures.length !== draft.features.length) {
      setDraft({ ...draft, features: retainedFeatures });
    }
    setNotice("All catalog features are already in this plan.");
  }

  function addCustomFeature() {
    if (!draft) return;
    setDraft({
      ...draft,
      features: [...draft.features, { featureKey: "", kind: "flag", enabled: true }],
    });
  }

  function removeFeature(index: number) {
    if (!draft) return;
    setDraft({ ...draft, features: draft.features.filter((_, featureIndex) => featureIndex !== index) });
  }

  async function savePlan() {
    if (!token || !draft) return;
    const metadata = draftMetadata();
    if (!metadata) return;
    const features = normalizeFeatures(draft.features);
    const payload = {
      tier: draft.tier.trim(),
      displayName: draft.displayName.trim(),
      description: draft.description.trim(),
      active: draft.active,
      features,
      metadata,
      reason: draft.reason,
    };
    const result = selectedPlan
      ? await run(() => api.updatePlan(token, selectedPlan.planId, payload), "Plan saved")
      : await run(() => api.createPlan(token, { ...payload, planId: draft.planId.trim() }), "Plan created");
    if (result) {
      setSelectedPlanId(result.plan.planId);
      await Promise.all([loadPlans(token, result.plan.planId), loadFeatureCatalog(token)]);
    }
  }

  async function previewPlan(action: "update" | "archive" | "publish") {
    if (!token || !selectedPlan || !draft) return;
    const metadata = action === "update" ? draftMetadata() : undefined;
    if (action === "update" && !metadata) return;
    const result = await run(() => api.previewPlan(token, selectedPlan.planId, {
      action,
      tier: draft.tier.trim(),
      displayName: draft.displayName.trim(),
      description: draft.description.trim(),
      active: draft.active,
      features: normalizeFeatures(draft.features),
      ...(metadata ? { metadata } : {}),
      sampleLimit: 25,
    }));
    if (result) setPreview(result.preview);
  }

  async function archivePlan() {
    if (!token || !selectedPlan || !draft) return;
    const result = await run(() => api.archivePlan(token, selectedPlan.planId, draft.reason), "Plan archived");
    if (result) await Promise.all([loadPlans(token, selectedPlan.planId), loadFeatureCatalog(token)]);
  }

  async function clonePlan() {
    if (!token || !selectedPlan || !draft || !cloneTargetPlanId.trim()) return;
    const result = await run(() => api.clonePlan(token, selectedPlan.planId, {
      targetPlanId: cloneTargetPlanId.trim(),
      displayName: `${draft.displayName} Copy`,
      reason: draft.reason,
    }), "Plan cloned");
    if (result) await Promise.all([loadPlans(token, result.plan.planId), loadFeatureCatalog(token)]);
  }

  async function selectWorkspace(workspace: PlatformWorkspaceSummary) {
    setTenantId(workspace.tenantId);
    setConnectorTenantId(workspace.tenantId);
    setOperationsTenantId(workspace.tenantId);
    setSubscriptionPlanId(workspace.planId);
    await loadWorkspace(token, workspace.tenantId);
  }

  async function updateSubscription() {
    if (!token || !tenantId.trim() || !subscriptionPlanId.trim()) return;
    const result = await run(() => api.updateWorkspaceSubscription(token, tenantId.trim(), {
      planId: subscriptionPlanId.trim(),
      status: "active",
      billingProvider: "manual",
      reason: subscriptionReason,
    }), "Subscription updated");
    if (result) {
      setSubscription(result.subscription);
      await Promise.all([loadWorkspace(token, tenantId), loadWorkspaces(token)]);
    }
  }

  async function transitionWorkspaceLifecycle(action: "suspend" | "reactivate") {
    if (!token || !tenantId.trim() || lifecycleReason.length < 8) return;
    const result = action === "suspend"
      ? await run(() => api.suspendWorkspace(token, tenantId.trim(), lifecycleReason), "Workspace suspended")
      : await run(() => api.reactivateWorkspace(token, tenantId.trim(), lifecycleReason), "Workspace reactivated");
    if (result) {
      await Promise.all([loadWorkspaces(token), loadWorkspace(token, result.workspace.summary.tenantId)]);
    }
  }

  async function createOverride() {
    if (!token || !tenantId.trim() || !overrideForm.featureKey.trim()) return;
    const governance = overrideGovernanceInput();
    if (!governance) return;
    const input: EntitlementOverrideInput = {
      ...overrideForm,
      featureKey: overrideForm.featureKey.trim(),
      expiresAt: overrideForm.expiresAt?.trim() || defaultOverrideExpiresAt(),
      governance,
      ...(overrideForm.limit !== undefined ? { limit: overrideForm.limit } : {}),
    };
    const result = await run(() => api.createEntitlementOverride(token, tenantId.trim(), input), "Override saved");
    if (result) await loadWorkspace(token, tenantId);
  }

  async function expireOverride(overrideId: string) {
    if (!token || !tenantId.trim()) return;
    const reason = overrideForm.reason.trim() || "Superadmin override expired";
    const result = await run(() => api.expireEntitlementOverride(token, tenantId.trim(), overrideId, reason), "Override expired");
    if (result) await loadWorkspace(token, tenantId);
  }

  async function setAvailabilityOverride(featureKey: string, enabled: boolean) {
    if (!token || !tenantId.trim()) return;
    const governance = overrideGovernanceInput();
    if (!governance) return;
    const reason = overrideForm.reason.trim() || `Superadmin ${enabled ? "enabled" : "disabled"} ${featureKey}`;
    const result = await run(() => api.createEntitlementOverride(token, tenantId.trim(), {
      featureKey,
      enabled,
      expiresAt: overrideForm.expiresAt?.trim() || defaultOverrideExpiresAt(),
      governance,
      reason,
      metadata: { source: "superadmin-availability-control" },
    }), enabled ? "Availability enabled" : "Availability disabled");
    if (result) await loadWorkspace(token, tenantId);
  }

  async function registerPublisher() {
    if (!token || !publisherForm.publisherId.trim() || !publisherForm.publicKeyBase64.trim()) return;
    const result = await run(() => api.registerPackPublisher(token, {
      publisherId: publisherForm.publisherId.trim(),
      displayName: publisherForm.displayName.trim(),
      contactEmail: publisherForm.contactEmail.trim(),
      publicKeyBase64: publisherForm.publicKeyBase64.trim(),
      ...(publisherForm.notes.trim() ? { notes: publisherForm.notes.trim() } : {}),
    }), "Publisher registered");
    if (result) await loadMarketplaceOps(token);
  }

  async function approvePublisher(publisherId: string) {
    if (!token) return;
    const result = await run(() => api.approvePackPublisher(token, publisherId), "Publisher approved");
    if (result) await loadMarketplaceOps(token);
  }

  async function suspendPublisher(publisherId: string) {
    if (!token || publisherSuspendReason.length < 8) return;
    const result = await run(() => api.suspendPackPublisher(token, publisherId, publisherSuspendReason), "Publisher suspended");
    if (result) await loadMarketplaceOps(token);
  }

  async function revokePack() {
    if (!token || !revocationForm.packId.trim() || !revocationForm.version.trim() || revocationForm.reason.length < 8) return;
    const result = await run(() => api.revokePack(token, {
      packId: revocationForm.packId.trim(),
      version: revocationForm.version.trim(),
      reason: revocationForm.reason,
    }), "Pack revoked");
    if (result) await loadMarketplaceOps(token);
  }

  async function unrevokePack(packId: string, version: string) {
    if (!token) return;
    const result = await run(() => api.unrevokePack(token, packId, version), "Pack revocation removed");
    if (result) await loadMarketplaceOps(token);
  }

  async function reviewMarketplacePack() {
    if (!token || !packReviewForm.packId.trim() || !packReviewForm.version.trim()) return;
    const evidenceUrls = packReviewForm.evidenceUrls
      .split("\n")
      .map((entry) => entry.trim())
      .filter(Boolean);
    const result = await run(() => api.reviewMarketplacePack(token, packReviewForm.packId.trim(), packReviewForm.version.trim(), {
      status: packReviewForm.status,
      ...(packReviewForm.notes.trim() ? { notes: packReviewForm.notes.trim() } : {}),
      ...(evidenceUrls.length > 0 ? { evidenceUrls } : {}),
    }), "Pack review updated");
    if (result) await loadMarketplaceOps(token);
  }

  async function moderateMarketplaceBuyerReview(review: MarketplacePackReviewEntry, status: MarketplaceBuyerReviewStatus) {
    if (!token) return;
    const result = await run(() => api.moderateMarketplaceReview(token, review.tenantId, review.reviewId, {
      status,
      ...(buyerReviewNotes.trim() ? { notes: buyerReviewNotes.trim() } : {}),
    }), "Buyer review updated");
    if (result) await loadMarketplaceOps(token);
  }

  function selectPackForReview(pack: MarketplacePackEntry) {
    setPackReviewForm({
      packId: pack.packId,
      version: pack.version,
      status: pack.review.status,
      notes: pack.review.notes ?? "",
      evidenceUrls: pack.review.evidenceUrls.join("\n"),
    });
  }

  async function inspectConnectorPlugin(plugin: ConnectorPluginEntry) {
    if (!token || !connectorTenantId.trim()) return;
    setConnectorForm({ ...connectorForm, pluginId: plugin.pluginId, version: plugin.version });
    const result = await run(() => api.connectorPlugin(token, connectorTenantId.trim(), plugin.pluginId, plugin.version));
    if (result) setConnectorPluginDetail(result.plugin);
  }

  async function transitionConnectorPlugin(transition: "approve" | "suspend" | "revoke") {
    if (!token || !connectorTenantId.trim() || !connectorForm.pluginId.trim() || !connectorForm.version.trim()) return;
    const needsReason = transition !== "approve";
    if (needsReason && connectorForm.reason.length < 8) return;
    const result = await run(() => api.transitionConnectorPlugin(
      token,
      connectorTenantId.trim(),
      connectorForm.pluginId.trim(),
      connectorForm.version.trim(),
      transition,
      connectorForm.reason.trim() || undefined,
    ), `Connector plugin ${transition} complete`);
    if (result) {
      setConnectorPluginDetail(result.plugin);
      await Promise.all([loadConnectorPlugins(token), loadAuditEvents(token)]);
    }
  }

  async function certifyConnectorPlugin() {
    if (!token || !connectorTenantId.trim() || !connectorForm.pluginId.trim() || !connectorForm.version.trim()) return;
    const result = await run(() => api.certifyConnectorPlugin(
      token,
      connectorTenantId.trim(),
      connectorForm.pluginId.trim(),
      connectorForm.version.trim(),
      connectorForm.notes.trim() || undefined,
    ), "Connector certification complete");
    if (result) setConnectorCertification(result.certification);
  }

  async function loadConnectorEvidencePack() {
    if (!token || !connectorTenantId.trim() || !connectorForm.pluginId.trim() || !connectorForm.version.trim()) return;
    const result = await run(() => api.connectorPluginEvidencePack(token, connectorTenantId.trim(), connectorForm.pluginId.trim(), connectorForm.version.trim()));
    if (result) setConnectorEvidencePack(result.evidencePack);
  }

  async function rollbackTenantRollout() {
    if (!token || !operationsTenantId.trim() || !operationsAgentId.trim() || operationsRollbackReason.length < 8) return;
    const result = await run(() => api.rollbackTenantRollout(
      token,
      operationsTenantId.trim(),
      operationsAgentId.trim(),
      operationsEnvironment,
      operationsRollbackReason,
    ), "Rollout rollback submitted");
    if (result) {
      setRolloutPlan(result.plan);
      await loadAuditEvents(token);
    }
  }

  async function captureRoiSnapshot() {
    if (!token || !operationsTenantId.trim()) return;
    const result = await run(() => api.createTenantRoiSnapshot(token, operationsTenantId.trim(), roiSnapshotType), "ROI snapshot captured");
    if (result) await loadTenantOperations(token);
  }

  async function rotateOpenAiCredential() {
    if (!token || openAiRotationKey.trim().length < 8 || openAiRotationReason.trim().length < 8) return;
    const result = await run(() => api.rotatePlatformOpenAiCredential(token, {
      apiKey: openAiRotationKey.trim(),
      reason: openAiRotationReason.trim(),
    }), "OpenAI key rotated");
    if (!result) return;
    setOpenAiRotationKey("");
    setOpenAiRotationReason("");
    setPlatformCredentials((current) => [
      result.provider,
      ...current.filter((credential) => credential.providerId !== result.provider.providerId),
    ]);
    await loadAuditEvents(token);
  }

  async function saveTransactionalEmailSettings() {
    if (!token) return;
    const smtpPort = Number.parseInt(transactionalEmailForm.smtpPort.trim(), 10);
    const result = await run(() => api.updatePlatformTransactionalEmailSettings(token, {
      provider: transactionalEmailForm.provider,
      fromEmail: transactionalEmailForm.fromEmail.trim(),
      appBaseUrl: transactionalEmailForm.appBaseUrl.trim(),
      ...(transactionalEmailForm.resendApiKey.trim() ? { resendApiKey: transactionalEmailForm.resendApiKey.trim() } : {}),
      ...(transactionalEmailForm.sendGridApiKey.trim() ? { sendGridApiKey: transactionalEmailForm.sendGridApiKey.trim() } : {}),
      ...(transactionalEmailForm.smtpHost.trim() ? { smtpHost: transactionalEmailForm.smtpHost.trim() } : {}),
      ...(Number.isInteger(smtpPort) && smtpPort > 0 ? { smtpPort } : {}),
      ...(transactionalEmailForm.smtpUsername.trim() ? { smtpUsername: transactionalEmailForm.smtpUsername.trim() } : {}),
      ...(transactionalEmailForm.smtpPassword.trim() ? { smtpPassword: transactionalEmailForm.smtpPassword.trim() } : {}),
      smtpSecure: transactionalEmailForm.smtpSecure,
      reason: transactionalEmailForm.reason.trim(),
    }), "Transactional email saved");
    if (!result) return;
    setTransactionalEmailSettings(result.settings);
    setTransactionalEmailForm((current) => ({
      ...current,
      provider: result.settings.provider,
      fromEmail: result.settings.fromEmail,
      appBaseUrl: result.settings.appBaseUrl,
      resendApiKey: "",
      sendGridApiKey: "",
      smtpHost: result.settings.smtpHost ?? "smtp.gmail.com",
      smtpPort: String(result.settings.smtpPort ?? 465),
      smtpUsername: result.settings.smtpUsername ?? "",
      smtpPassword: "",
      smtpSecure: result.settings.smtpSecure,
      reason: "",
    }));
    await loadAuditEvents(token);
  }

  async function sendTransactionalEmailTest() {
    if (!token || !transactionalEmailTestRecipient.trim()) return;
    if (transactionalEmailForm.provider === "log") {
      setNotice(null);
      setError("Choose Resend, SendGrid, or Google Workspace / SMTP before sending a test email.");
      return;
    }
    if (transactionalEmailSettings?.provider !== transactionalEmailForm.provider) {
      setNotice(null);
      setError("Save the current email settings before sending a test email.");
      return;
    }
    const result = await run(() => api.sendPlatformTransactionalEmailTest(token, {
      toEmail: transactionalEmailTestRecipient.trim(),
    }), "Test email sent");
    if (!result) return;
    setTransactionalEmailTestResult(result);
    setTransactionalEmailSettings(result.settings);
    await loadAuditEvents(token);
  }

  return (
    <main className="shell">
      <header className="topbar">
        <div className="brand-lockup">
          <ShieldCheck size={26} />
          <div>
            <h1>AIOSCP Superadmin</h1>
            <p>{user ? `${user.email} · ${user.role}` : "Platform operations"}</p>
          </div>
        </div>
        <div className="topbar-actions">
          {token ? (
            <>
              {!passwordChangeRequired ? <button className="icon-button" type="button" onClick={() => void refreshSection()} disabled={loading} title="Refresh current section">
                <RefreshCw size={18} />
              </button> : null}
              <button className="text-button" type="button" onClick={logout}>
                <LogOut size={17} /> Sign out
              </button>
            </>
          ) : null}
        </div>
      </header>

      {!token ? (
        <section className="auth-panel">
          <form className="tool-card login-form" onSubmit={(event) => void handleLogin(event)}>
            <h2>Operator Login</h2>
            <label>
              Email
              <input value={email} onChange={(event) => setEmail(event.currentTarget.value)} type="email" autoComplete="email" />
            </label>
            <label>
              Password
              <input value={password} onChange={(event) => setPassword(event.currentTarget.value)} type="password" autoComplete="current-password" />
            </label>
            <button className="primary-button" type="submit" disabled={loading || !email || !password}>
              <LogIn size={18} /> Sign in
            </button>
          </form>
        </section>
      ) : passwordChangeRequired ? (
        renderAccountSection(true)
      ) : (
        <>
          <nav className="app-nav" aria-label="Superadmin sections">
            <button className={section === "plans" ? "selected" : ""} type="button" onClick={() => setSection("plans")}>
              <Blocks size={17} /> Plans
            </button>
            <button className={section === "features" ? "selected" : ""} type="button" onClick={() => setSection("features")}>
              <Table2 size={17} /> Features
            </button>
            <button className={section === "workspaces" ? "selected" : ""} type="button" onClick={() => setSection("workspaces")}>
              <Building2 size={17} /> Workspaces
            </button>
            <button className={section === "marketplace" ? "selected" : ""} type="button" onClick={() => setSection("marketplace")}>
              <BadgeCheck size={17} /> Marketplace
            </button>
            <button className={section === "operations" ? "selected" : ""} type="button" onClick={() => setSection("operations")}>
              <PlayCircle size={17} /> Operations
            </button>
            <button className={section === "credentials" ? "selected" : ""} type="button" onClick={() => setSection("credentials")}>
              <KeyRound size={17} /> Credentials
            </button>
            <button className={section === "email" ? "selected" : ""} type="button" onClick={() => setSection("email")}>
              <Mail size={17} /> Email
            </button>
            <button className={section === "audit" ? "selected" : ""} type="button" onClick={() => setSection("audit")}>
              <ScrollText size={17} /> Audit
            </button>
            <button className={section === "account" ? "selected" : ""} type="button" onClick={() => setSection("account")}>
              <UserRound size={17} /> Account
            </button>
          </nav>

          {section === "plans" ? (
            <section className="plan-workbench">
              <aside className="panel plan-list-panel">
                <div className="section-title">
                  <Blocks size={19} />
                  <h2>Plans</h2>
                </div>
                <button className="primary-button full-width" type="button" onClick={startNewPlan}>
                  <Plus size={17} /> New plan
                </button>
                <div className="plan-list">
                  {plans.map((plan) => (
                    <button
                      className={`plan-row ${plan.planId === selectedPlanId ? "selected" : ""}`}
                      key={plan.planId}
                      type="button"
                      onClick={() => setSelectedPlanId(plan.planId)}
                    >
                      <span>
                        <strong>{plan.displayName}</strong>
                        <small>{plan.planId}</small>
                      </span>
                      <span className={`status-pill ${plan.active ? "live" : "draft"}`}>{statusTone(plan)}</span>
                    </button>
                  ))}
                </div>
              </aside>

              <section className="panel editor-panel">
                <div className="section-title">
                  <SlidersHorizontal size={19} />
                  <h2>{selectedPlan ? "Plan Editor" : "New Plan"}</h2>
                </div>
                {draft ? (
                  <div className="editor-grid">
                    <div className="inline-fields">
                      <label>
                        Plan ID
                        <input value={draft.planId} disabled={Boolean(selectedPlan)} onChange={(event) => setDraft({ ...draft, planId: event.currentTarget.value })} />
                      </label>
                      <label>
                        Tier
                        <input value={draft.tier} onChange={(event) => setDraft({ ...draft, tier: event.currentTarget.value })} />
                      </label>
                    </div>
                    <label>
                      Display name
                      <input value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.currentTarget.value })} />
                    </label>
                    <label>
                      Description
                      <textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.currentTarget.value })} rows={3} />
                    </label>
                    <label className="toggle-line">
                      <input checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.currentTarget.checked })} type="checkbox" />
                      Active plan
                    </label>
                    <div className="feature-editor">
                      <div className="subsection-title">
                        <Table2 size={17} />
                        <h3>Features</h3>
                        <div className="subsection-actions">
                          <button className="text-button compact" type="button" onClick={addFeature} title="Add all missing catalog features">
                            <Plus size={16} /> Add
                          </button>
                          <button className="text-button compact" type="button" onClick={addCustomFeature} title="Add one custom feature row">
                            Custom
                          </button>
                        </div>
                      </div>
                      <datalist id="known-feature-keys">
                        {featureRegistry.map((featureKey) => <option value={featureKey} key={featureKey} />)}
                      </datalist>
                      {draft.features.length === 0 ? <div className="empty-state small">No features on this plan</div> : null}
                      {draft.features.length > 0 ? (
                        <div className="feature-row feature-row-header" aria-hidden="true">
                          <span>Feature key</span>
                          <span>Kind</span>
                          <span>Enabled</span>
                          <span>Limit</span>
                          <span>Unit</span>
                          <span>Description</span>
                          <span />
                        </div>
                      ) : null}
                      {draft.features.map((feature, index) => (
                        <div className="feature-row" key={`${feature.featureKey}-${index}`}>
                          <input list="known-feature-keys" value={feature.featureKey} onChange={(event) => updateFeature(index, { featureKey: event.currentTarget.value })} aria-label="Feature key" />
                          <select value={feature.kind} onChange={(event) => updateFeature(index, { kind: event.currentTarget.value as PlanFeature["kind"] })} aria-label="Feature kind">
                            <option value="flag">Flag</option>
                            <option value="quota">Quota</option>
                          </select>
                          <label className="toggle-line compact-toggle">
                            <input checked={feature.enabled} onChange={(event) => updateFeature(index, { enabled: event.currentTarget.checked })} type="checkbox" />
                            On
                          </label>
                          <input value={feature.limit ?? ""} onChange={(event) => updateFeature(index, { limit: event.currentTarget.value ? Number(event.currentTarget.value) : undefined })} type="number" min={0} disabled={feature.kind === "flag"} aria-label="Feature limit" />
                          <input value={feature.unit ?? ""} onChange={(event) => updateFeature(index, { unit: event.currentTarget.value || undefined })} aria-label="Feature unit" />
                          <input value={feature.description ?? ""} onChange={(event) => updateFeature(index, { description: event.currentTarget.value || undefined })} aria-label="Feature description" />
                          <button className="icon-button" type="button" onClick={() => removeFeature(index)} title="Remove feature">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="metadata-editor">
                      <div className="subsection-title">
                        <SlidersHorizontal size={17} />
                        <h3>Metadata Contract</h3>
                      </div>
                      <div className="metadata-grid">
                        <label>
                          Sort order
                          <input value={numberInputValue(draftMetadataRecord, "sortOrder")} onChange={(event) => setMetadataNumber("sortOrder", event.currentTarget.value)} type="number" />
                        </label>
                        <label>
                          Monthly price cents
                          <input value={numberInputValue(draftMetadataRecord, "priceMonthlyCents")} onChange={(event) => setMetadataNumber("priceMonthlyCents", event.currentTarget.value)} type="number" min={0} />
                        </label>
                        <label>
                          Billing mode
                          <select value={(stringValue(draftMetadataRecord, "billingMode") || parsedDraftMetadata?.billingMode || "manual") as PlanBillingMode} onChange={(event) => setMetadataString("billingMode", event.currentTarget.value)}>
                            {PLAN_BILLING_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}
                          </select>
                        </label>
                        <label>
                          Pricing label
                          <input value={stringValue(draftMetadataRecord, "pricingLabel")} onChange={(event) => setMetadataString("pricingLabel", event.currentTarget.value)} />
                        </label>
                        <label>
                          Theme
                          <input value={stringValue(draftMetadataRecord, "theme")} onChange={(event) => setMetadataString("theme", event.currentTarget.value)} />
                        </label>
                        <label>
                          Badge
                          <input value={stringValue(draftMetadataRecord, "badge")} onChange={(event) => setMetadataString("badge", event.currentTarget.value)} />
                        </label>
                        <label className="toggle-line metadata-toggle">
                          <input checked={booleanValue(draftMetadataRecord, "public", parsedDraftMetadata?.public ?? false)} onChange={(event) => setMetadataBoolean("public", event.currentTarget.checked)} type="checkbox" />
                          Public
                        </label>
                        <label className="toggle-line metadata-toggle">
                          <input checked={booleanValue(draftMetadataRecord, "highlighted", parsedDraftMetadata?.highlighted ?? false)} onChange={(event) => setMetadataBoolean("highlighted", event.currentTarget.checked)} type="checkbox" />
                          Highlighted
                        </label>
                      </div>
                      <div className="metadata-grid three-column">
                        <label>
                          CTA label
                          <input value={nestedStringValue(draftMetadataRecord, "cta", "label")} onChange={(event) => setNestedMetadataString("cta", "label", event.currentTarget.value)} />
                        </label>
                        <label>
                          CTA kind
                          <select value={(nestedStringValue(draftMetadataRecord, "cta", "kind") || parsedDraftMetadata?.cta.kind || "disabled") as PlanCtaKind} onChange={(event) => setNestedMetadataString("cta", "kind", event.currentTarget.value)}>
                            {PLAN_CTA_KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                          </select>
                        </label>
                        <label>
                          CTA href
                          <input value={nestedStringValue(draftMetadataRecord, "cta", "href")} onChange={(event) => setNestedMetadataString("cta", "href", event.currentTarget.value)} />
                        </label>
                      </div>
                      <div className="metadata-toggle-grid">
                        {VISIBILITY_KEYS.map(([key, label]) => (
                          <label className="toggle-line metadata-toggle" key={key}>
                            <input checked={nestedBooleanValue(draftMetadataRecord, "visibility", key, parsedDraftMetadata?.visibility[key] ?? false)} onChange={(event) => setNestedMetadataBoolean("visibility", key, event.currentTarget.checked)} type="checkbox" />
                            {label}
                          </label>
                        ))}
                        {CHANGE_POLICY_KEYS.map(([key, label]) => (
                          <label className="toggle-line metadata-toggle" key={key}>
                            <input checked={nestedBooleanValue(draftMetadataRecord, "changePolicy", key, parsedDraftMetadata?.changePolicy[key] ?? false)} onChange={(event) => setNestedMetadataBoolean("changePolicy", key, event.currentTarget.checked)} type="checkbox" />
                            {label}
                          </label>
                        ))}
                      </div>
                      <div className="metadata-grid three-column">
                        {COMMERCIAL_METADATA_TEXT_FIELDS.map(([key, label]) => (
                          <label key={key}>
                            {label}
                            <input value={stringValue(draftMetadataRecord, key)} onChange={(event) => setMetadataString(key, event.currentTarget.value)} />
                          </label>
                        ))}
                        <label>
                          Deployment types
                          <input value={stringArrayInputValue(draftMetadataRecord, "deploymentTypes")} onChange={(event) => setMetadataStringArray("deploymentTypes", event.currentTarget.value)} />
                        </label>
                        <label>
                          Audit retention days
                          <input value={numberInputValue(draftMetadataRecord, "auditRetentionDays")} onChange={(event) => setMetadataNumber("auditRetentionDays", event.currentTarget.value)} type="number" min={0} />
                        </label>
                        <label>
                          Trace retention days
                          <input value={numberInputValue(draftMetadataRecord, "traceRetentionDays")} onChange={(event) => setMetadataNumber("traceRetentionDays", event.currentTarget.value)} type="number" min={0} />
                        </label>
                      </div>
                      <div className="metadata-grid two-column">
                        {MODEL_ACCESS_FIELDS.map(([key, label]) => (
                          <label key={key}>
                            {label}
                            {key === "platformModelIds" ? (
                              <select
                                multiple
                                size={Math.min(8, Math.max(4, platformModelOptions.length))}
                                value={parseStringArrayInput(nestedStringArrayInputValue(draftMetadataRecord, "modelAccess", key))}
                                onChange={(event) => setNestedMetadataStringArrayValues(
                                  "modelAccess",
                                  key,
                                  Array.from(event.currentTarget.selectedOptions).map((option) => option.value),
                                )}
                              >
                                {platformModelOptions.map((modelId) => <option value={modelId} key={modelId}>{modelId}</option>)}
                              </select>
                            ) : (
                              <input value={nestedStringArrayInputValue(draftMetadataRecord, "modelAccess", key)} onChange={(event) => setNestedMetadataStringArray("modelAccess", key, event.currentTarget.value)} placeholder="comma-separated values" />
                            )}
                          </label>
                        ))}
                      </div>
                      {metadataValidationIssues.length > 0 ? (
                        <div className="validation-panel">
                          {metadataValidationIssues.map((issue) => <span key={`${issue.path}-${issue.message}`}>{issue.path}: {issue.message}</span>)}
                        </div>
                      ) : null}
                      <label>
                        Advanced metadata JSON
                        <textarea className="code-input" value={draft.metadataText} onChange={(event) => setDraft({ ...draft, metadataText: event.currentTarget.value })} rows={8} spellCheck={false} />
                      </label>
                    </div>
                    <label>
                      Reason
                      <input value={draft.reason} onChange={(event) => setDraft({ ...draft, reason: event.currentTarget.value })} />
                    </label>
                    <div className="button-row">
                      <button className="primary-button" type="button" onClick={() => void savePlan()} disabled={loading || draft.reason.length < 8}>
                        <Save size={17} /> {selectedPlan ? "Save" : "Create"}
                      </button>
                      <button className="text-button" type="button" onClick={() => void previewPlan("update")} disabled={loading || !selectedPlan}>
                        <PlayCircle size={17} /> Preview
                      </button>
                      <button className="text-button" type="button" onClick={() => void previewPlan("archive")} disabled={loading || !selectedPlan}>
                        <Archive size={17} /> Impact
                      </button>
                    </div>
                    {selectedPlan ? (
                      <div className="clone-line">
                        <input value={cloneTargetPlanId} onChange={(event) => setCloneTargetPlanId(event.currentTarget.value)} />
                        <button className="text-button" type="button" onClick={() => void clonePlan()} disabled={loading || draft.reason.length < 8}>
                          <CopyPlus size={17} /> Clone
                        </button>
                        <button className="danger-button" type="button" onClick={() => void archivePlan()} disabled={loading || draft.reason.length < 8}>
                          <Archive size={17} /> Archive
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="empty-state">No plan selected</div>
                )}
              </section>

              <aside className="panel impact-panel">
                <div className="section-title">
                  <BadgeCheck size={19} />
                  <h2>Impact</h2>
                </div>
                {preview ? (
                  <div className="impact-stack">
                    <div className="metric-strip">
                      <span>Affected</span>
                      <strong>{preview.impact.activeSubscriptionCount}</strong>
                    </div>
                    <div className="metric-strip">
                      <span>Features</span>
                      <strong>{preview.impact.featureChanges.added.length + preview.impact.featureChanges.removed.length + preview.impact.featureChanges.changed.length}</strong>
                    </div>
                    <div className="risk-list">
                      {preview.impact.risks.length === 0 ? <span className="status-pill live">clear</span> : preview.impact.risks.map((risk) => (
                        <span className="status-pill warning" key={risk}>{risk}</span>
                      ))}
                    </div>
                    <pre>{formatJson(preview.impact)}</pre>
                  </div>
                ) : (
                  <div className="empty-state">No preview</div>
                )}
              </aside>

              <section className="panel feature-matrix-panel">
                <div className="section-title">
                  <Table2 size={19} />
                  <h2>Feature Matrix</h2>
                </div>
                {featureRegistry.length === 0 ? <div className="empty-state">No catalog features yet</div> : (
                  <div className="matrix-scroll">
                    <table className="feature-matrix">
                      <thead>
                        <tr>
                          <th>Feature</th>
                          {plans.map((plan) => <th key={plan.planId}>{plan.planId}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {featureRegistry.map((featureKey) => (
                          <tr key={featureKey}>
                            <td>{featureKey}</td>
                            {plans.map((plan) => <td key={`${featureKey}-${plan.planId}`}>{featureValue(plan, featureKey)}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </section>
          ) : section === "features" ? (
            <section className="feature-catalog-workbench panel">
              <div className="section-title">
                <Table2 size={19} />
                <h2>Feature Catalog</h2>
              </div>
              <div className="workspace-summary-strip">
                <span>{features.length} feature keys</span>
                <span>{features.filter((feature) => feature.kind === "quota").length} quotas</span>
                <span>{features.filter((feature) => feature.kind === "mixed").length} mixed definitions</span>
              </div>
              <div className="matrix-scroll">
                <table className="feature-matrix feature-catalog-table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      <th>Kind</th>
                      <th>Enabled Plans</th>
                      <th>Quota Plans</th>
                      <th>Plan Coverage</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature.featureKey}>
                        <td>{feature.featureKey}</td>
                        <td><span className={`status-pill ${feature.kind === "quota" ? "warning" : "live"}`}>{feature.kind}</span></td>
                        <td>{feature.enabledPlanIds.join(", ") || "-"}</td>
                        <td>{feature.quotaPlans.map((plan) => `${plan.planId}:${plan.limit ?? "unlimited"}${plan.unit ? ` ${plan.unit}` : ""}`).join(", ") || "-"}</td>
                        <td>{feature.sources.map((source) => `${source.planId} ${source.enabled ? "on" : "off"}`).join(", ")}</td>
                        <td>{feature.description ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {features.length === 0 ? <div className="empty-state small">No platform features are registered in plans yet</div> : null}
            </section>
          ) : section === "workspaces" ? (
            <section className="workspace-workbench">
              <section className="panel activation-panel">
                <div className="section-title">
                  <BadgeCheck size={19} />
                  <h2>Activation Funnel</h2>
                </div>
                {workspaceActivationAnalytics ? (
                  <>
                    <div className="marketplace-analytics-grid">
                      <div className="metric-strip">
                        <span>Workspaces</span>
                        <strong>{workspaceActivationAnalytics.workspaceCount}</strong>
                      </div>
                      <div className="metric-strip">
                        <span>Completed</span>
                        <strong>{workspaceActivationAnalytics.completedCount}</strong>
                      </div>
                      <div className="metric-strip">
                        <span>Pending</span>
                        <strong>{workspaceActivationAnalytics.pendingCount}</strong>
                      </div>
                      <div className="metric-strip">
                        <span>Completion</span>
                        <strong>{formatPercent(workspaceActivationAnalytics.completionRate)}</strong>
                      </div>
                    </div>
                    <div className="marketplace-analytics-grid detail-grid">
                      <div className="analytics-list">
                        <strong>Plan funnel</strong>
                        {workspaceActivationAnalytics.planBuckets.slice(0, 5).map((bucket) => (
                          <span key={`${bucket.planId}-${bucket.planTier}`}>{bucket.planId}: {bucket.completedCount}/{bucket.workspaceCount} complete · {bucket.blockedCount} blocked</span>
                        ))}
                        {workspaceActivationAnalytics.planBuckets.length === 0 ? <span>No plan buckets yet</span> : null}
                      </div>
                      <div className="analytics-list">
                        <strong>Top blockers</strong>
                        {workspaceActivationAnalytics.blockerBuckets.slice(0, 5).map((bucket) => (
                          <span key={`${bucket.blockerId}-${bucket.severity}`}>{bucket.blockerId}: {bucket.count} · {bucket.sampleTenantIds.join(", ")}</span>
                        ))}
                        {workspaceActivationAnalytics.blockerBuckets.length === 0 ? <span>No active blockers</span> : null}
                      </div>
                      <div className="analytics-list">
                        <strong>Completion sources</strong>
                        {workspaceActivationAnalytics.sourceBuckets.slice(0, 5).map((bucket) => (
                          <span key={bucket.source}>{bucket.source}: {bucket.count}</span>
                        ))}
                        {workspaceActivationAnalytics.sourceBuckets.length === 0 ? <span>No completions yet</span> : null}
                      </div>
                      <div className="analytics-list">
                        <strong>Recent completions</strong>
                        {workspaceActivationAnalytics.recentCompletions.slice(0, 5).map((entry) => (
                          <span key={`${entry.tenantId}-${entry.occurredAt}`}>{entry.workspaceName}: {entry.source}</span>
                        ))}
                        {workspaceActivationAnalytics.recentCompletions.length === 0 ? <span>No recent completions</span> : null}
                      </div>
                    </div>
                  </>
                ) : <div className="empty-state small">No activation analytics loaded</div>}
              </section>

              <section className="panel workspace-list-panel">
                <div className="section-title">
                  <Search size={19} />
                  <h2>Workspace Search</h2>
                </div>
                <div className="workspace-search-grid">
                  <label>
                    Search
                    <input value={workspaceQuery} onChange={(event) => setWorkspaceQuery(event.currentTarget.value)} />
                  </label>
                  <label>
                    Status
                    <select value={workspaceStatus} onChange={(event) => setWorkspaceStatus(event.currentTarget.value as WorkspaceStatusFilter)}>
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadWorkspaces()} disabled={loading}>
                    <RefreshCw size={17} /> Search
                  </button>
                </div>
                <div className="workspace-list">
                  {workspaces.map((workspace) => {
                    const attention = workspace.usage.quotas.filter((quota) => quota.warningLevel !== "ok").length;
                    const blockers = onboardingBlockerCount(workspace);
                    return (
                      <button className={`workspace-card ${workspace.tenantId === tenantId ? "selected" : ""}`} type="button" key={workspace.tenantId} onClick={() => void selectWorkspace(workspace)}>
                        <span>
                          <strong>{workspace.name}</strong>
                          <small>{workspace.tenantId}</small>
                        </span>
                        <span className="workspace-card-meta">
                          <span className={`status-pill ${workspace.status === "active" ? "live" : "warning"}`}>{workspace.status}</span>
                          <span className={`status-pill ${onboardingTone(workspace)}`}>{blockers > 0 ? `${blockers} onboarding blockers` : onboardingLabel(workspace)}</span>
                          <small>{workspace.planId} · {isolationLabel(workspace.isolationTier)} · {attention} quota alerts</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="panel workspace-panel">
                <div className="section-title">
                  <WalletCards size={19} />
                  <h2>Workspace Operations</h2>
                </div>
                <div className="workspace-controls">
                  <label>
                    Tenant ID
                    <input value={tenantId} onChange={(event) => setTenantId(event.currentTarget.value)} />
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadWorkspace()} disabled={loading || !tenantId.trim()}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
                {selectedWorkspace ? (
                  <>
                    <div className="workspace-summary-strip">
                      <span>{selectedWorkspace.memberCount} members</span>
                      <span>{selectedWorkspace.entitlements.enabledFeatureCount} features</span>
                      <span>{selectedWorkspace.entitlements.quotaFeatureCount} quotas</span>
                      <span>{onboardingLabel(selectedWorkspace)}</span>
                      <span>{onboardingBlockerCount(selectedWorkspace)} onboarding blockers</span>
                    </div>
                    {selectedWorkspace.onboarding?.blockers.length ? (
                      <div className="override-list">
                        {selectedWorkspace.onboarding.blockers.map((blocker) => (
                          <div className="override-row" key={blocker.blockerId}>
                            <span>
                              <strong>{blocker.message}</strong>
                              <small>{blocker.severity} · {blocker.blockerId}</small>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
                <div className="lifecycle-controls">
                  <label>
                    Lifecycle reason
                    <input value={lifecycleReason} onChange={(event) => setLifecycleReason(event.currentTarget.value)} />
                  </label>
                  <div className="button-row">
                    <button className="danger-button" type="button" onClick={() => void transitionWorkspaceLifecycle("suspend")} disabled={loading || lifecycleReason.length < 8 || selectedWorkspace?.status === "suspended"}>
                      <Archive size={17} /> Suspend
                    </button>
                    <button className="text-button" type="button" onClick={() => void transitionWorkspaceLifecycle("reactivate")} disabled={loading || lifecycleReason.length < 8 || selectedWorkspace?.status === "active"}>
                      <PlayCircle size={17} /> Reactivate
                    </button>
                  </div>
                </div>
                <div className="workspace-split">
                  <div className="workspace-column">
                    <h3>Subscription</h3>
                    <label>
                      Plan ID
                      <input value={subscriptionPlanId} onChange={(event) => setSubscriptionPlanId(event.currentTarget.value)} />
                    </label>
                    <label>
                      Reason
                      <input value={subscriptionReason} onChange={(event) => setSubscriptionReason(event.currentTarget.value)} />
                    </label>
                    <button className="primary-button" type="button" onClick={() => void updateSubscription()} disabled={loading || subscriptionReason.length < 8}>
                      <Save size={17} /> Update
                    </button>
                    {subscription ? <pre>{formatJson(subscription)}</pre> : <div className="empty-state">No subscription</div>}
                  </div>
                  <div className="workspace-column">
                    <h3>Override</h3>
                    <label>
                      Feature key
                      <input list="known-feature-keys" value={overrideForm.featureKey} onChange={(event) => setOverrideForm({ ...overrideForm, featureKey: event.currentTarget.value })} />
                    </label>
                    <div className="inline-fields">
                      <label>
                        Limit
                        <input value={overrideForm.limit ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, limit: event.currentTarget.value ? Number(event.currentTarget.value) : undefined })} type="number" min={0} />
                      </label>
                      <label className="toggle-line">
                        <input checked={overrideForm.enabled ?? false} onChange={(event) => setOverrideForm({ ...overrideForm, enabled: event.currentTarget.checked })} type="checkbox" />
                        Enabled
                      </label>
                    </div>
                    <label>
                      Unit
                      <input value={overrideForm.unit ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, unit: event.currentTarget.value || undefined })} />
                    </label>
                    <label>
                      Expires at
                      <input value={overrideForm.expiresAt ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, expiresAt: event.currentTarget.value })} />
                    </label>
                    <div className="inline-fields">
                      <label>
                        Approval ticket
                        <input value={overrideForm.governance?.ticketId ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, governance: mergeOverrideGovernance(overrideForm.governance, { ticketId: event.currentTarget.value }) })} />
                      </label>
                      <label>
                        Approved by
                        <input value={overrideForm.governance?.approvedBy ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, governance: mergeOverrideGovernance(overrideForm.governance, { approvedBy: event.currentTarget.value }) })} />
                      </label>
                    </div>
                    <div className="inline-fields">
                      <label>
                        Risk tier
                        <select value={overrideForm.governance?.riskTier ?? "medium"} onChange={(event) => setOverrideForm({ ...overrideForm, governance: mergeOverrideGovernance(overrideForm.governance, { riskTier: event.currentTarget.value as EntitlementOverrideGovernanceInput["riskTier"] }) })}>
                          {OVERRIDE_RISK_TIERS.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                        </select>
                      </label>
                      <label>
                        Approval workflow ID
                        <input value={overrideForm.governance?.approvalId ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, governance: mergeOverrideGovernance(overrideForm.governance, { approvalId: event.currentTarget.value }) })} />
                      </label>
                    </div>
                    <label>
                      Governance comment
                      <input value={overrideForm.governance?.comment ?? ""} onChange={(event) => setOverrideForm({ ...overrideForm, governance: mergeOverrideGovernance(overrideForm.governance, { comment: event.currentTarget.value }) })} />
                    </label>
                    <label>
                      Reason
                      <input value={overrideForm.reason} onChange={(event) => setOverrideForm({ ...overrideForm, reason: event.currentTarget.value })} />
                    </label>
                    <button className="primary-button" type="button" onClick={() => void createOverride()} disabled={loading || overrideForm.reason.length < 8 || !overrideForm.expiresAt?.trim() || !overrideForm.governance?.ticketId.trim() || !overrideForm.governance?.approvedBy.trim() || (requiresApprovalWorkflow(overrideForm.governance?.riskTier) && !overrideForm.governance?.approvalId?.trim())}>
                      <Save size={17} /> Save
                    </button>
                    <div className="override-list">
                      {overrides.map((override) => (
                        <div className="override-row" key={override.overrideId}>
                          <span>
                            <strong>{override.featureKey}</strong>
                            <small>{override.limit ?? (override.enabled ? "enabled" : "disabled")}</small>
                          </span>
                          <button className="icon-button" type="button" onClick={() => void expireOverride(override.overrideId)} title="Expire override">
                            <Archive size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="availability-presets">
                      <h3>Availability Presets</h3>
                      {AVAILABILITY_FEATURE_PRESETS.map((preset) => (
                        <div className="ops-row" key={preset.featureKey}>
                          <span>
                            <strong>{preset.label}</strong>
                            <small>{preset.featureKey}</small>
                          </span>
                          <button className="text-button compact" type="button" onClick={() => void setAvailabilityOverride(preset.featureKey, true)} disabled={loading || !tenantId.trim()}>
                            Enable
                          </button>
                          <button className="danger-button" type="button" onClick={() => void setAvailabilityOverride(preset.featureKey, false)} disabled={loading || !tenantId.trim()}>
                            Disable
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </section>
          ) : section === "marketplace" ? (
            <section className="marketplace-workbench">
              <section className="panel ops-panel">
                <div className="section-title">
                  <BadgeCheck size={19} />
                  <h2>Publisher Governance</h2>
                </div>
                <div className="ops-filter-grid">
                  <label>
                    Status
                    <select value={publisherStatus} onChange={(event) => setPublisherStatus(event.currentTarget.value as PublisherStatusFilter)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="verified">Verified</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadMarketplaceOps()} disabled={loading}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
                <div className="publisher-list">
                  {publishers.map((publisher) => (
                    <div className="ops-row" key={publisher.publisherId}>
                      <span>
                        <strong>{publisher.displayName}</strong>
                        <small>{publisher.publisherId} · {publisher.contactEmail}</small>
                      </span>
                      <span className={`status-pill ${publisher.status === "verified" ? "live" : "warning"}`}>{publisher.status}</span>
                      <button className="text-button compact" type="button" onClick={() => void approvePublisher(publisher.publisherId)} disabled={loading || publisher.status === "verified"}>
                        Approve
                      </button>
                      <button className="danger-button" type="button" onClick={() => void suspendPublisher(publisher.publisherId)} disabled={loading || publisherSuspendReason.length < 8 || publisher.status === "suspended"}>
                        Suspend
                      </button>
                    </div>
                  ))}
                  {publishers.length === 0 ? <div className="empty-state small">No publishers match the current filter</div> : null}
                </div>
                <label className="ops-reason">
                  Suspend reason
                  <input value={publisherSuspendReason} onChange={(event) => setPublisherSuspendReason(event.currentTarget.value)} />
                </label>
              </section>

              <section className="panel ops-panel">
                <div className="section-title">
                  <Plus size={19} />
                  <h2>Register Publisher</h2>
                </div>
                <div className="ops-form-grid">
                  <label>
                    Publisher ID
                    <input value={publisherForm.publisherId} onChange={(event) => setPublisherForm({ ...publisherForm, publisherId: event.currentTarget.value })} placeholder="publisher:example" />
                  </label>
                  <label>
                    Display name
                    <input value={publisherForm.displayName} onChange={(event) => setPublisherForm({ ...publisherForm, displayName: event.currentTarget.value })} />
                  </label>
                  <label>
                    Contact email
                    <input value={publisherForm.contactEmail} onChange={(event) => setPublisherForm({ ...publisherForm, contactEmail: event.currentTarget.value })} type="email" />
                  </label>
                  <label>
                    Public key
                    <input value={publisherForm.publicKeyBase64} onChange={(event) => setPublisherForm({ ...publisherForm, publicKeyBase64: event.currentTarget.value })} />
                  </label>
                  <label className="wide-field">
                    Notes
                    <input value={publisherForm.notes} onChange={(event) => setPublisherForm({ ...publisherForm, notes: event.currentTarget.value })} />
                  </label>
                  <button className="primary-button" type="button" onClick={() => void registerPublisher()} disabled={loading || !publisherForm.publisherId.trim() || !publisherForm.publicKeyBase64.trim()}>
                    <Save size={17} /> Register
                  </button>
                </div>
              </section>

              <section className="panel ops-panel marketplace-wide-panel">
                <div className="section-title">
                  <Table2 size={19} />
                  <h2>Install Analytics</h2>
                </div>
                <div className="marketplace-analytics-grid">
                  <div className="metric-strip">
                    <span>Attempts</span>
                    <strong>{marketplaceInstallAnalytics?.attemptCount ?? 0}</strong>
                  </div>
                  <div className="metric-strip">
                    <span>Installed</span>
                    <strong>{marketplaceInstallAnalytics?.installCount ?? 0}</strong>
                  </div>
                  <div className="metric-strip">
                    <span>Failed</span>
                    <strong>{marketplaceInstallAnalytics?.failedInstallCount ?? 0}</strong>
                  </div>
                  <div className="metric-strip">
                    <span>Activated</span>
                    <strong>{formatPercent(marketplaceInstallAnalytics?.activationSuccessRate ?? 0)}</strong>
                  </div>
                </div>
                <div className="marketplace-analytics-grid detail-grid">
                  <div className="analytics-list">
                    <strong>Failure reasons</strong>
                    {(marketplaceInstallAnalytics?.failureReasons ?? []).slice(0, 4).map((reason) => (
                      <span key={reason.reasonCode}>{reason.reasonCode}: {reason.count}</span>
                    ))}
                    {(marketplaceInstallAnalytics?.failureReasons.length ?? 0) === 0 ? <span>No failed installs recorded</span> : null}
                  </div>
                  <div className="analytics-list">
                    <strong>Top packs</strong>
                    {(marketplaceInstallAnalytics?.packs ?? []).slice(0, 4).map((pack) => (
                      <span key={`${pack.packId}-${pack.version ?? "latest"}`}>{pack.packId}: {pack.installCount} installed, {pack.failedInstallCount} failed</span>
                    ))}
                    {(marketplaceInstallAnalytics?.packs.length ?? 0) === 0 ? <span>No marketplace install events recorded</span> : null}
                  </div>
                  <div className="analytics-list">
                    <strong>Activation blockers</strong>
                    {(marketplaceInstallAnalytics?.activationBlockers ?? []).slice(0, 6).map((blocker) => (
                      <span key={blocker.section}>{blocker.section}: {blocker.count} pending</span>
                    ))}
                    {(marketplaceInstallAnalytics?.activationBlockers.length ?? 0) === 0 ? <span>No pending activation blockers recorded</span> : null}
                  </div>
                </div>
              </section>

              <section className="panel ops-panel marketplace-wide-panel">
                <div className="section-title">
                  <ShieldCheck size={19} />
                  <h2>Buyer Reviews</h2>
                </div>
                <div className="ops-filter-grid review-filter-grid">
                  <label>
                    Review status
                    <select value={buyerReviewStatus} onChange={(event) => setBuyerReviewStatus(event.currentTarget.value as BuyerReviewStatusFilter)}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="all">All</option>
                    </select>
                  </label>
                  <label className="wide-field">
                    Moderation notes
                    <input value={buyerReviewNotes} onChange={(event) => setBuyerReviewNotes(event.currentTarget.value)} />
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadMarketplaceOps()} disabled={loading}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
                <div className="matrix-scroll">
                  <table className="feature-matrix marketplace-pack-table">
                    <thead>
                      <tr>
                        <th>Rating</th>
                        <th>Review</th>
                        <th>Pack</th>
                        <th>Tenant</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplaceReviews.map((review) => (
                        <tr key={review.reviewId}>
                          <td>{review.rating}/5</td>
                          <td>{review.title ?? review.body ?? review.reviewId}</td>
                          <td>{review.packId}@{review.version}</td>
                          <td>{review.tenantId}</td>
                          <td><span className={`status-pill ${review.status === "approved" ? "live" : review.status === "rejected" ? "warning" : "draft"}`}>{review.status}</span></td>
                          <td>
                            <button className="text-button compact" type="button" onClick={() => void moderateMarketplaceBuyerReview(review, "approved")} disabled={loading || review.status === "approved"}>
                              Approve
                            </button>
                            <button className="danger-button" type="button" onClick={() => void moderateMarketplaceBuyerReview(review, "rejected")} disabled={loading || review.status === "rejected"}>
                              Reject
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {marketplaceReviews.length === 0 ? <div className="empty-state small">No buyer reviews match the current filter</div> : null}
              </section>

              <section className="panel ops-panel marketplace-wide-panel">
                <div className="section-title">
                  <ScrollText size={19} />
                  <h2>Pack Review Queue</h2>
                </div>
                <div className="ops-filter-grid review-filter-grid">
                  <label>
                    Publisher
                    <input value={packPublisherId} onChange={(event) => setPackPublisherId(event.currentTarget.value)} />
                  </label>
                  <label>
                    Pack status
                    <select value={packStatus} onChange={(event) => setPackStatus(event.currentTarget.value as PackStatusFilter)}>
                      <option value="active">Active</option>
                      <option value="yanked">Yanked</option>
                      <option value="all">All</option>
                    </select>
                  </label>
                  <label>
                    Review
                    <select value={packReviewStatus} onChange={(event) => setPackReviewStatus(event.currentTarget.value as ReviewStatusFilter)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="community_review">Community review</option>
                      <option value="approved">Approved</option>
                      <option value="changes_requested">Changes requested</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadMarketplaceOps()} disabled={loading}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
                <div className="matrix-scroll">
                  <table className="feature-matrix marketplace-pack-table">
                    <thead>
                      <tr>
                        <th>Pack</th>
                        <th>Publisher</th>
                        <th>Status</th>
                        <th>Review</th>
                        <th>Commercial</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplacePacks.map((pack) => (
                        <tr key={`${pack.packId}-${pack.version}`}>
                          <td>{pack.packId}@{pack.version}</td>
                          <td>{pack.publisherId}</td>
                          <td><span className={`status-pill ${pack.status === "active" && !pack.revoked ? "live" : "warning"}`}>{pack.revoked ? "revoked" : pack.status}</span></td>
                          <td>{pack.review.status}</td>
                          <td>{pack.commercial && "model" in pack.commercial ? String(pack.commercial.model) : "free"}</td>
                          <td>
                            <button className="text-button compact" type="button" onClick={() => selectPackForReview(pack)}>
                              Review
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {marketplacePacks.length === 0 ? <div className="empty-state small">No marketplace packs match the current review filters</div> : null}
                <div className="ops-form-grid review-form-grid">
                  <label>
                    Pack ID
                    <input value={packReviewForm.packId} onChange={(event) => setPackReviewForm({ ...packReviewForm, packId: event.currentTarget.value })} />
                  </label>
                  <label>
                    Version
                    <input value={packReviewForm.version} onChange={(event) => setPackReviewForm({ ...packReviewForm, version: event.currentTarget.value })} />
                  </label>
                  <label>
                    Review status
                    <select value={packReviewForm.status} onChange={(event) => setPackReviewForm({ ...packReviewForm, status: event.currentTarget.value as MarketplacePackReviewStatus })}>
                      <option value="pending">Pending</option>
                      <option value="community_review">Community review</option>
                      <option value="approved">Approved</option>
                      <option value="changes_requested">Changes requested</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </label>
                  <label className="wide-field">
                    Notes
                    <input value={packReviewForm.notes} onChange={(event) => setPackReviewForm({ ...packReviewForm, notes: event.currentTarget.value })} />
                  </label>
                  <label className="wide-field">
                    Evidence URLs
                    <textarea value={packReviewForm.evidenceUrls} onChange={(event) => setPackReviewForm({ ...packReviewForm, evidenceUrls: event.currentTarget.value })} rows={3} />
                  </label>
                  <button className="primary-button" type="button" onClick={() => void reviewMarketplacePack()} disabled={loading || !packReviewForm.packId.trim() || !packReviewForm.version.trim()}>
                    <BadgeCheck size={17} /> Save review
                  </button>
                </div>
              </section>

              <section className="panel ops-panel">
                <div className="section-title">
                  <Archive size={19} />
                  <h2>Pack Revocations</h2>
                </div>
                <div className="ops-form-grid">
                  <label>
                    Pack ID
                    <input value={revocationForm.packId} onChange={(event) => setRevocationForm({ ...revocationForm, packId: event.currentTarget.value })} />
                  </label>
                  <label>
                    Version
                    <input value={revocationForm.version} onChange={(event) => setRevocationForm({ ...revocationForm, version: event.currentTarget.value })} />
                  </label>
                  <label className="wide-field">
                    Reason
                    <input value={revocationForm.reason} onChange={(event) => setRevocationForm({ ...revocationForm, reason: event.currentTarget.value })} />
                  </label>
                  <button className="danger-button" type="button" onClick={() => void revokePack()} disabled={loading || revocationForm.reason.length < 8}>
                    <Archive size={17} /> Revoke
                  </button>
                </div>
                <div className="publisher-list">
                  {revocations.map((revocation) => (
                    <div className="ops-row" key={`${revocation.packId}-${revocation.version}`}>
                      <span>
                        <strong>{revocation.packId}@{revocation.version}</strong>
                        <small>{revocation.reason}</small>
                      </span>
                      <span className="status-pill warning">revoked</span>
                      <button className="text-button compact" type="button" onClick={() => void unrevokePack(revocation.packId, revocation.version)} disabled={loading}>
                        Reinstate
                      </button>
                    </div>
                  ))}
                  {revocations.length === 0 ? <div className="empty-state small">No active pack revocations</div> : null}
                </div>
              </section>

              <section className="panel ops-panel marketplace-wide-panel">
                <div className="section-title">
                  <ShieldCheck size={19} />
                  <h2>Connector Plugin Registry</h2>
                </div>
                <div className="ops-filter-grid connector-filter-grid">
                  <label>
                    Tenant ID
                    <input value={connectorTenantId} onChange={(event) => setConnectorTenantId(event.currentTarget.value)} />
                  </label>
                  <label>
                    Status
                    <select value={connectorStatus} onChange={(event) => setConnectorStatus(event.currentTarget.value as ConnectorPluginStatusFilter)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="suspended">Suspended</option>
                      <option value="revoked">Revoked</option>
                    </select>
                  </label>
                  <label>
                    Adapter
                    <select value={connectorAdapter} onChange={(event) => setConnectorAdapter(event.currentTarget.value as ConnectorPluginAdapterFilter)}>
                      <option value="all">All</option>
                      <option value="http">HTTP</option>
                      <option value="process">Process</option>
                      <option value="mcp">MCP</option>
                    </select>
                  </label>
                  <button className="text-button" type="button" onClick={() => void loadConnectorPlugins()} disabled={loading || !connectorTenantId.trim()}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
                <div className="matrix-scroll">
                  <table className="feature-matrix connector-plugin-table">
                    <thead>
                      <tr>
                        <th>Plugin</th>
                        <th>Adapter</th>
                        <th>Status</th>
                        <th>Connections</th>
                        <th>Agents</th>
                        <th>Packs</th>
                        <th>Updated</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectorPlugins.map((plugin) => (
                        <tr key={`${plugin.pluginId}-${plugin.version}`}>
                          <td>
                            <strong>{plugin.displayName}</strong>
                            <small>{plugin.pluginId}@{plugin.version}</small>
                          </td>
                          <td>{plugin.adapter}</td>
                          <td><span className={`status-pill ${plugin.status === "approved" ? "live" : "warning"}`}>{plugin.status}</span></td>
                          <td>{plugin.impact ? `${plugin.impact.activeConnectionCount}/${plugin.impact.affectedConnectionCount}` : "-"}</td>
                          <td>{plugin.impact?.affectedAgentIds.length ?? "-"}</td>
                          <td>{plugin.impact?.affectedPackInstallCount ?? "-"}</td>
                          <td>{plugin.updatedAt ? new Date(plugin.updatedAt).toLocaleString() : "-"}</td>
                          <td>
                            <button className="text-button compact" type="button" onClick={() => void inspectConnectorPlugin(plugin)}>
                              Inspect
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {connectorPlugins.length === 0 ? <div className="empty-state small">No connector plugins match this tenant scope</div> : null}
                <div className="ops-form-grid connector-action-grid">
                  <label>
                    Plugin ID
                    <input value={connectorForm.pluginId} onChange={(event) => setConnectorForm({ ...connectorForm, pluginId: event.currentTarget.value })} />
                  </label>
                  <label>
                    Version
                    <input value={connectorForm.version} onChange={(event) => setConnectorForm({ ...connectorForm, version: event.currentTarget.value })} />
                  </label>
                  <label className="wide-field">
                    Notes
                    <input value={connectorForm.notes} onChange={(event) => setConnectorForm({ ...connectorForm, notes: event.currentTarget.value })} />
                  </label>
                  <label className="wide-field">
                    Transition reason
                    <input value={connectorForm.reason} onChange={(event) => setConnectorForm({ ...connectorForm, reason: event.currentTarget.value })} />
                  </label>
                  <button className="text-button" type="button" onClick={() => void transitionConnectorPlugin("approve")} disabled={loading || !connectorForm.pluginId.trim() || !connectorForm.version.trim()}>
                    Approve
                  </button>
                  <button className="danger-button" type="button" onClick={() => void transitionConnectorPlugin("suspend")} disabled={loading || !connectorForm.pluginId.trim() || !connectorForm.version.trim() || connectorForm.reason.length < 8}>
                    Suspend
                  </button>
                  <button className="danger-button" type="button" onClick={() => void transitionConnectorPlugin("revoke")} disabled={loading || !connectorForm.pluginId.trim() || !connectorForm.version.trim() || connectorForm.reason.length < 8}>
                    Revoke
                  </button>
                  <button className="primary-button" type="button" onClick={() => void certifyConnectorPlugin()} disabled={loading || !connectorTenantId.trim() || !connectorForm.pluginId.trim() || !connectorForm.version.trim()}>
                    <BadgeCheck size={17} /> Certify
                  </button>
                  <button className="text-button" type="button" onClick={() => void loadConnectorEvidencePack()} disabled={loading || !connectorTenantId.trim() || !connectorForm.pluginId.trim() || !connectorForm.version.trim()}>
                    Evidence pack
                  </button>
                </div>
                {connectorPluginDetail?.impact ? (
                  <div className="impact-stack connector-impact-stack">
                    <div>
                      <span>Affected connections</span>
                      <strong>{connectorPluginDetail.impact.affectedConnectionCount}</strong>
                      <small>{connectorPluginDetail.impact.activeConnectionCount} active · {connectorPluginDetail.impact.disabledConnectionCount} disabled</small>
                    </div>
                    <div>
                      <span>Trigger-backed agents</span>
                      <strong>{connectorPluginDetail.impact.affectedAgentIds.length}</strong>
                      <small>{connectorPluginDetail.impact.affectedAutomationTriggerCount} connector event triggers</small>
                    </div>
                    <div>
                      <span>Installed packs</span>
                      <strong>{connectorPluginDetail.impact.affectedPackInstallCount}</strong>
                      <small>{connectorPluginDetail.impact.recentTriggerEventCount} recent trigger events sampled</small>
                    </div>
                    <div>
                      <span>Manifest surface</span>
                      <strong>{connectorPluginDetail.impact.actionCount}</strong>
                      <small>{connectorPluginDetail.impact.triggerEventCount} declared trigger events</small>
                    </div>
                  </div>
                ) : null}
                <div className="operations-json-grid connector-json-grid">
                  <div>
                    <h3>Detail</h3>
                    {connectorPluginDetail ? <pre>{formatJson(connectorPluginDetail)}</pre> : <div className="empty-state small">No connector plugin selected</div>}
                  </div>
                  <div>
                    <h3>Certification</h3>
                    {connectorCertification ? <pre>{formatJson(connectorCertification)}</pre> : <div className="empty-state small">No connector certification run in this session</div>}
                  </div>
                  <div>
                    <h3>Evidence Pack</h3>
                    {connectorEvidencePack ? <pre>{formatJson(connectorEvidencePack)}</pre> : <div className="empty-state small">No evidence pack loaded</div>}
                  </div>
                </div>
              </section>
            </section>
          ) : section === "operations" ? (
            <section className="operations-workbench">
              <section className="panel ops-panel">
                <div className="section-title">
                  <Server size={19} />
                  <h2>Local Runner Fleet Posture</h2>
                </div>
                {fleetOverview ? (
                  <>
                    <div className="marketplace-analytics-grid">
                      <div className="metric-strip">
                        <span>Runners</span>
                        <strong>{fleetOverview.summary.totalLocalRunnerCount.toLocaleString()}</strong>
                        <small>{fleetOverview.summary.onlineLocalRunnerCount.toLocaleString()} online</small>
                      </div>
                      <div className="metric-strip">
                        <span>Offline</span>
                        <strong>{fleetOverview.summary.offlineLocalRunnerCount.toLocaleString()}</strong>
                        <small>heartbeat stale or stopped</small>
                      </div>
                      <div className="metric-strip">
                        <span>Version drift</span>
                        <strong>{fleetOverview.summary.outdatedLocalRunnerCount.toLocaleString()}</strong>
                        <small>behind latest reported version</small>
                      </div>
                      <div className="metric-strip">
                        <span>Task saturation</span>
                        <strong>{formatWholePercent(fleetOverview.summary.localRunnerSaturationPercent)}</strong>
                        <small>{formatCapacity(fleetOverview.summary.localRunnerCurrentTaskCount, fleetOverview.summary.localRunnerTaskCapacity)} slots</small>
                      </div>
                    </div>
                    <div className="matrix-scroll">
                      <table className="feature-matrix operations-table">
                        <thead>
                          <tr>
                            <th>Workspace</th>
                            <th>Health</th>
                            <th>Online</th>
                            <th>Offline</th>
                            <th>Assignments</th>
                            <th>Queued</th>
                            <th>Version</th>
                            <th>Attention</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fleetOverview.workspaces
                            .filter((entry) => entry.localRunners.totalRunnerCount > 0 || entry.localRunners.assignmentCount > 0)
                            .sort((left, right) => right.localRunners.offlineRunnerCount - left.localRunners.offlineRunnerCount
                              || right.localRunners.outdatedRunnerCount - left.localRunners.outdatedRunnerCount
                              || right.localRunners.queuedTaskCount - left.localRunners.queuedTaskCount)
                            .slice(0, 12)
                            .map((entry) => (
                              <tr key={entry.workspace.tenantId}>
                                <td>{entry.workspace.name}<br /><small>{entry.workspace.tenantId}</small></td>
                                <td><span className={`status-pill ${entry.health === "healthy" ? "live" : entry.health === "critical" ? "warning" : "draft"}`}>{entry.health}</span></td>
                                <td>{entry.localRunners.onlineRunnerCount}</td>
                                <td>{entry.localRunners.offlineRunnerCount}</td>
                                <td>{entry.localRunners.assignmentCount}</td>
                                <td>{entry.localRunners.queuedTaskCount}</td>
                                <td>{entry.localRunners.latestVersion ?? "-"}</td>
                                <td>{entry.localRunners.attentionItems[0] ?? entry.attentionItems[0] ?? "No runner attention"}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {fleetOverview.workspaces.every((entry) => entry.localRunners.totalRunnerCount === 0 && entry.localRunners.assignmentCount === 0)
                      ? <div className="empty-state small">No local runners are registered across the fleet.</div>
                      : null}
                    <p className="helper-text">Generated {new Date(fleetOverview.generatedAt).toLocaleString()}</p>
                  </>
                ) : <div className="empty-state small">No platform fleet posture loaded</div>}
              </section>

              <section className="panel ops-panel operations-control-panel">
                <div className="section-title">
                  <SlidersHorizontal size={19} />
                  <h2>Tenant Operations Scope</h2>
                </div>
                <div className="ops-form-grid">
                  <label>
                    Tenant ID
                    <input value={operationsTenantId} onChange={(event) => setOperationsTenantId(event.currentTarget.value)} />
                  </label>
                  <label>
                    Agent ID
                    <input value={operationsAgentId} onChange={(event) => setOperationsAgentId(event.currentTarget.value)} />
                  </label>
                  <label>
                    Environment
                    <select value={operationsEnvironment} onChange={(event) => setOperationsEnvironment(event.currentTarget.value as OperationsEnvironment)}>
                      <option value="dev">Dev</option>
                      <option value="staging">Staging</option>
                      <option value="production">Production</option>
                    </select>
                  </label>
                  <button className="primary-button" type="button" onClick={() => void loadTenantOperations()} disabled={loading || !operationsTenantId.trim()}>
                    <RefreshCw size={17} /> Load
                  </button>
                </div>
              </section>

              <section className="panel ops-panel">
                <div className="section-title">
                  <PlayCircle size={19} />
                  <h2>Release Oversight</h2>
                </div>
                <div className="matrix-scroll">
                  <table className="feature-matrix operations-table">
                    <thead>
                      <tr>
                        <th>Release</th>
                        <th>Agent</th>
                        <th>Version</th>
                        <th>Environment</th>
                        <th>Status</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {releases.map((release) => (
                        <tr key={release.releaseId}>
                          <td>{release.releaseId}</td>
                          <td>{release.agentId}</td>
                          <td>{release.version ?? "-"}</td>
                          <td>{release.environment ?? "-"}</td>
                          <td>{release.status ?? "-"}</td>
                          <td>{release.updatedAt ? new Date(release.updatedAt).toLocaleString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {releases.length === 0 ? <div className="empty-state small">No releases match this tenant scope</div> : null}
              </section>

              <section className="panel ops-panel">
                <div className="section-title">
                  <Archive size={19} />
                  <h2>Rollout Control</h2>
                </div>
                <label>
                  Rollback reason
                  <input value={operationsRollbackReason} onChange={(event) => setOperationsRollbackReason(event.currentTarget.value)} />
                </label>
                <div className="button-row">
                  <button className="danger-button" type="button" onClick={() => void rollbackTenantRollout()} disabled={loading || !operationsAgentId.trim() || operationsRollbackReason.length < 8}>
                    <Archive size={17} /> Roll back canary
                  </button>
                </div>
                {rolloutPlan ? <pre>{formatJson(rolloutPlan)}</pre> : <div className="empty-state small">No rollout plan loaded for this agent/environment</div>}
              </section>

              <section className="panel ops-panel">
                <div className="section-title">
                  <WalletCards size={19} />
                  <h2>ROI Evidence</h2>
                </div>
                <div className="ops-form-grid compact-ops-form">
                  <label>
                    Snapshot type
                    <select value={roiSnapshotType} onChange={(event) => setRoiSnapshotType(event.currentTarget.value as RoiSnapshotType)}>
                      <option value="manual">Manual</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="qbr">QBR</option>
                      <option value="renewal">Renewal</option>
                    </select>
                  </label>
                  <button className="primary-button" type="button" onClick={() => void captureRoiSnapshot()} disabled={loading || !operationsTenantId.trim()}>
                    <Save size={17} /> Capture snapshot
                  </button>
                </div>
                <div className="operations-json-grid">
                  <div>
                    <h3>Summary</h3>
                    {roiSummary ? <pre>{formatJson(roiSummary)}</pre> : <div className="empty-state small">No ROI summary loaded</div>}
                  </div>
                  <div>
                    <h3>Workflows</h3>
                    {roiWorkflows.length > 0 ? <pre>{formatJson(roiWorkflows)}</pre> : <div className="empty-state small">No ROI workflows</div>}
                  </div>
                  <div>
                    <h3>Snapshots</h3>
                    {roiSnapshots.length > 0 ? <pre>{formatJson(roiSnapshots)}</pre> : <div className="empty-state small">No ROI snapshots</div>}
                  </div>
                </div>
              </section>
            </section>
          ) : section === "credentials" ? (
            <section className="credentials-workbench">
              <section className="panel credentials-panel">
                <div className="section-title">
                  <KeyRound size={19} />
                  <h2>Platform Provider Credentials</h2>
                </div>
                <div className="credential-summary-grid">
                  {platformCredentials.map((credential) => (
                    <div className="credential-card" key={credential.providerId}>
                      <div>
                        <strong>{credential.displayName}</strong>
                        <small>{credential.providerId}</small>
                      </div>
                      <span className={`status-pill ${credential.health.available ? "live" : credential.configured ? "warning" : "draft"}`}>
                        {credential.health.available ? "healthy" : credential.configured ? "configured" : "missing"}
                      </span>
                      <div className="credential-meta">
                        <span>Source</span>
                        <strong>{credential.source}</strong>
                        <span>Last 4</span>
                        <strong>{credential.last4 ?? "-"}</strong>
                        <span>Models</span>
                        <strong>{credential.models.length.toLocaleString()}</strong>
                        <span>Checked</span>
                        <strong>{new Date(credential.health.lastCheckedAt).toLocaleString()}</strong>
                      </div>
                    </div>
                  ))}
                  {platformCredentials.length === 0 ? <div className="empty-state small">No platform provider credential status loaded</div> : null}
                </div>
              </section>

              <section className="panel credentials-panel">
                <div className="section-title">
                  <RefreshCw size={19} />
                  <h2>Rotate OpenAI Key</h2>
                </div>
                <div className="credential-form-grid">
                  <label>
                    New OpenAI API key
                    <input value={openAiRotationKey} onChange={(event) => setOpenAiRotationKey(event.currentTarget.value)} type="password" autoComplete="new-password" />
                  </label>
                  <label>
                    Change reason
                    <input value={openAiRotationReason} onChange={(event) => setOpenAiRotationReason(event.currentTarget.value)} />
                  </label>
                  <button className="primary-button" type="button" onClick={() => void rotateOpenAiCredential()} disabled={loading || openAiRotationKey.trim().length < 8 || openAiRotationReason.trim().length < 8}>
                    <Save size={17} /> Rotate key
                  </button>
                </div>
                {openAiCredential ? <pre>{formatJson({ ...openAiCredential, last4: openAiCredential.last4 ?? null })}</pre> : null}
              </section>
            </section>
          ) : section === "email" ? (
            <section className="credentials-workbench">
              <section className="panel credentials-panel">
                <div className="section-title">
                  <Mail size={19} />
                  <h2>Transactional Email</h2>
                </div>
                <div className="credential-summary-grid">
                  <div className="credential-card">
                    <div>
                      <strong>{transactionalEmailProviderLabel(transactionalEmailSettings?.provider)}</strong>
                      <small>Delivery provider</small>
                    </div>
                    <span className={`status-pill ${transactionalEmailSettings?.provider === "log" ? "draft" : isTransactionalEmailProviderConfigured(transactionalEmailSettings) ? "live" : "warning"}`}>
                      {transactionalEmailSettings?.provider === "log" ? "local log" : isTransactionalEmailProviderConfigured(transactionalEmailSettings) ? "configured" : "missing secret"}
                    </span>
                    <div className="credential-meta">
                      <span>Source</span>
                      <strong>{transactionalEmailSettings?.source ?? "default"}</strong>
                      <span>App URL</span>
                      <strong>{transactionalEmailSettings?.appBaseUrl ?? "-"}</strong>
                      <span>From</span>
                      <strong>{transactionalEmailSettings?.fromEmail ?? "-"}</strong>
                      <span>Resend key</span>
                      <strong>{transactionalEmailSettings?.resendConfigured ? `••••${transactionalEmailSettings.resendLast4 ?? "set"}` : "missing"}</strong>
                      <span>SendGrid key</span>
                      <strong>{transactionalEmailSettings?.sendGridConfigured ? `••••${transactionalEmailSettings.sendGridLast4 ?? "set"}` : "missing"}</strong>
                      <span>SMTP host</span>
                      <strong>{transactionalEmailSettings?.smtpHost ?? "-"}</strong>
                      <span>SMTP user</span>
                      <strong>{transactionalEmailSettings?.smtpUsername ?? "-"}</strong>
                      <span>SMTP password</span>
                      <strong>{transactionalEmailSettings?.smtpPasswordConfigured ? `••••${transactionalEmailSettings.smtpPasswordLast4 ?? "set"}` : "missing"}</strong>
                      <span>SMTP TLS</span>
                      <strong>{transactionalEmailSettings?.smtpSecure ? "SSL/TLS" : "STARTTLS"}</strong>
                    </div>
                  </div>
                </div>
              </section>

              <section className="panel credentials-panel">
                <div className="section-title">
                  <Save size={19} />
                  <h2>Email Delivery Settings</h2>
                </div>
                <div className="credential-form-grid">
                  <label>
                    Provider
                    <select value={transactionalEmailForm.provider} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, provider: event.currentTarget.value as TransactionalEmailProvider })}>
                      <option value="log">Local log only</option>
                      <option value="resend">Resend</option>
                      <option value="sendgrid">SendGrid</option>
                      <option value="smtp">Google Workspace / SMTP</option>
                    </select>
                  </label>
                  <label>
                    Sender email
                    <input value={transactionalEmailForm.fromEmail} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, fromEmail: event.currentTarget.value })} placeholder="AIOSCP Platform <no-reply@example.com>" />
                  </label>
                  <label>
                    App URL
                    <input value={transactionalEmailForm.appBaseUrl} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, appBaseUrl: event.currentTarget.value })} placeholder="http://localhost:3000" />
                  </label>
                  <label>
                    Resend API key
                    <input value={transactionalEmailForm.resendApiKey} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, resendApiKey: event.currentTarget.value })} type="password" autoComplete="new-password" placeholder={transactionalEmailSettings?.resendConfigured ? "Leave blank to keep existing key" : "Required for Resend"} />
                  </label>
                  <label>
                    SendGrid API key
                    <input value={transactionalEmailForm.sendGridApiKey} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, sendGridApiKey: event.currentTarget.value })} type="password" autoComplete="new-password" placeholder={transactionalEmailSettings?.sendGridConfigured ? "Leave blank to keep existing key" : "Required for SendGrid"} />
                  </label>
                  <label>
                    SMTP host
                    <input value={transactionalEmailForm.smtpHost} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, smtpHost: event.currentTarget.value })} placeholder="smtp.gmail.com" />
                  </label>
                  <label>
                    SMTP port
                    <input value={transactionalEmailForm.smtpPort} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, smtpPort: event.currentTarget.value })} inputMode="numeric" placeholder="465" />
                  </label>
                  <label>
                    SMTP username
                    <input value={transactionalEmailForm.smtpUsername} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, smtpUsername: event.currentTarget.value })} placeholder="admin@example.com" />
                    <small>Use the full Google Workspace email address.</small>
                  </label>
                  <label>
                    SMTP password
                    <input value={transactionalEmailForm.smtpPassword} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, smtpPassword: event.currentTarget.value })} type="password" autoComplete="new-password" placeholder={transactionalEmailSettings?.smtpPasswordConfigured ? "Leave blank to keep existing password" : "Google app password or SMTP relay secret"} />
                    <small>For Gmail SMTP, paste a Google app password. Normal mailbox passwords are rejected.</small>
                  </label>
                  <label className="checkbox-line">
                    <input checked={transactionalEmailForm.smtpSecure} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, smtpSecure: event.currentTarget.checked })} type="checkbox" />
                    SSL/TLS
                  </label>
                  <label>
                    Change reason
                    <input value={transactionalEmailForm.reason} onChange={(event) => setTransactionalEmailForm({ ...transactionalEmailForm, reason: event.currentTarget.value })} placeholder="Configure password reset delivery" />
                  </label>
                  <button className="primary-button" type="button" onClick={() => void saveTransactionalEmailSettings()} disabled={loading || transactionalEmailForm.fromEmail.trim().length < 3 || transactionalEmailForm.appBaseUrl.trim().length < 8 || transactionalEmailForm.reason.trim().length < 8}>
                    <Save size={17} /> Save email settings
                  </button>
                </div>
              </section>

              <section className="panel credentials-panel">
                <div className="section-title">
                  <Send size={19} />
                  <h2>Send Test Email</h2>
                </div>
                <div className="credential-form-grid">
                  <label>
                    Recipient
                    <input value={transactionalEmailTestRecipient} onChange={(event) => setTransactionalEmailTestRecipient(event.currentTarget.value)} placeholder="rushilinfra@gmail.com" />
                  </label>
                  <button className="primary-button" type="button" onClick={() => void sendTransactionalEmailTest()} disabled={loading || !testEmailRecipientReady || !testEmailSelectedProviderCanSend}>
                    <Send size={17} /> Send test email
                  </button>
                </div>
                {testEmailGuidance ? <small>{testEmailGuidance}</small> : null}
                {transactionalEmailTestResult ? (
                  <pre>{formatJson({
                    provider: transactionalEmailTestResult.delivery.provider,
                    delivered: transactionalEmailTestResult.delivery.delivered,
                    messageId: transactionalEmailTestResult.delivery.messageId ?? null,
                    appBaseUrl: transactionalEmailTestResult.settings.appBaseUrl,
                  })}</pre>
                ) : null}
              </section>
            </section>
          ) : section === "account" ? (
            renderAccountSection(false)
          ) : (
            <section className="audit-workbench panel">
              <div className="section-title">
                <ScrollText size={19} />
                <h2>Audit Events</h2>
              </div>
              <div className="audit-filter-grid">
                <label>
                  Tenant ID
                  <input value={auditTenantId} onChange={(event) => setAuditTenantId(event.currentTarget.value)} />
                </label>
                <label>
                  Action
                  <input value={auditAction} onChange={(event) => setAuditAction(event.currentTarget.value)} />
                </label>
                <label>
                  Resource
                  <input value={auditResource} onChange={(event) => setAuditResource(event.currentTarget.value)} />
                </label>
                <button className="text-button" type="button" onClick={() => void loadAuditEvents()} disabled={loading}>
                  <RefreshCw size={17} /> Load
                </button>
              </div>
              <div className="matrix-scroll">
                <table className="feature-matrix audit-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Tenant</th>
                      <th>Action</th>
                      <th>Resource</th>
                      <th>Decision</th>
                      <th>Rule</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEvents.map((entry) => (
                      <tr key={entry.entryId}>
                        <td>{new Date(entry.timestamp).toLocaleString()}</td>
                        <td>{entry.tenantId}</td>
                        <td>{entry.action}</td>
                        <td>{entry.resource}</td>
                        <td>{entry.decision}</td>
                        <td>{entry.matchedRuleId}</td>
                        <td>{entry.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {auditEvents.length === 0 ? <div className="empty-state small">No audit events match the current filters</div> : null}
            </section>
          )}
        </>
      )}

      <div className="toast-zone" aria-live="polite">
        {notice ? <div className="toast success">{notice}</div> : null}
        {error ? <div className="toast error">{error}</div> : null}
      </div>
    </main>
  );
}
